export type SupportedLocale = 'pl' | 'en' | 'es';

type NavItem = {
  label: string;
  href: string;
  type?: 'anchor' | 'path';
};

type Card = {
  title: string;
  description: string;
};

type TimelineStep = Card;

type Testimonial = {
  quote: string;
  author: string;
};

type ContactDetail = {
  label: string;
  value: string;
  href?: string;
};

type ContactField = {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
};

type BlogImage = {
  src: string;
  alt: string;
  caption?: string;
};

type BlogSection = {
  heading: string;
  paragraphs: string[];
  list?: string[];
  listStyle?: 'default' | 'paw'; // 'paw' for Paragraf Zwierzoochronny
  images?: BlogImage[];
};

type BlogPostTranslation = {
  title?: string;
  teaser?: string;
  sections?: BlogSection[];
  ctaTitle?: string;
  ctaText?: string;
};

export type BlogPost = {
  slug: string;
  title: string;
  category: string;
  teaser: string;
  date: string;
  readingTime: string;
  link: string;
  featured?: boolean;
  sections?: BlogSection[];
  relatedSlugs?: string[];
  isParagrafZwierzoochronny?: boolean; // Flag for special styling
  tags?: string[]; // Tags for categorization
  featuredImage?: BlogImage; // Featured image for article header
  ctaTitle?: string;
  ctaText?: string;
  translations?: Partial<Record<SupportedLocale, BlogPostTranslation>>;
};

type Content = {
  nav: NavItem[];
  hero: {
    id: string;
    eyebrow: string;
    title: string;
    lead: string[];
    bullets: string[];
    ctas: {
      primary: { label: string; href: string };
      secondary: { label: string; href: string };
    };
    portraitAlt: string;
  };
  forWhom: {
    id: string;
    title: string;
    intro: string;
    cards: Card[];
    linkText: string;
  };
  specialisations: {
    id: string;
    title: string;
    intro: string;
    items: Card[];
  };
  whyMe: {
    id: string;
    title: string;
    lead: string;
    usps: Card[];
  };
  aboutProfessional: {
    id: string;
    title: string;
    paragraphs: string[];
    blogCtaText: string;
    blogCtaLink: string;
  };
  aboutPrivate: {
    id: string;
    title: string;
    paragraphs: string[];
  };
  process: {
    id: string;
    title: string;
    steps: TimelineStep[];
  };
  testimonials: {
    id: string;
    items: Testimonial[];
  };
  blog: {
    teaser: {
      id: string;
      title: string;
      subtitle: string;
      ctaLabel: string;
      ctaLink: string;
      featuredSlugs: string[];
    };
    posts: BlogPost[];
  };
  contact: {
    id: string;
    title: string;
    officeHeading: string;
    details: ContactDetail[];
    helper: string;
    fields: ContactField[];
    consentLabel: string;
consentLinkLabel: string;
    submitLabel: string;
  };
  footer: {
    copyright: string;
    privacy: string;
    cookiesSettings: string;
    terms: string;
    socials: { name: string; href: string }[];
  };
};

const blogPosts: BlogPost[] = [
  {
    slug: 'umowa-adopcyjna',
    title: 'Co warto wiedzieć o umowie adopcyjnej?',
    category: 'Paragraf Zwierzoochronny',
    teaser: 'Co zawiera umowa adopcyjna zwierzęcia? Dlaczego adopcja nie czyni Cię właścicielem zwierzęcia?',
    date: '28 listopada 2024',
    readingTime: '2 min czytania',
    link: '/blog/umowa-adopcyjna.html',
    featured: true,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/umowa-adopcyjna.png',
      alt: 'Grafika ilustrująca umowę adopcyjną zwierzęcia',
    },
    tags: [
      'adopcja kota',
      'adopcja psa',
      'adopcja zwierząt',
      'obowiązki opiekuna',
      'prawo zwierząt',
      'schronisko',
      'umowa adopcyjna',
    ],
    sections: [
  {
    heading: '',
    paragraphs: [
      'Adopcja zwierzęcia ze schroniska to piękna decyzja, ale też konkretne zobowiązanie – także prawne. Umowa adopcyjna określa, jak ma wyglądać opieka nad zwierzęciem po opuszczeniu przez niego schroniska.',
      '',
      'Zachęcam do lektury niniejszego artykułu celem zapoznania się z tę tematyką.',
    ],
  },
  {
    heading: 'Czy adopcja sprawia, że stajesz się „właścicielem” zwierzęcia?',
    paragraphs: [
      'Nie.',
      '',
      'I dokładnie tak samo jest wtedy, gdy zwierzę kupisz albo znajdziesz.',
      '',
      'Do znudzenia będę przypominał, że **zwierzę nie jest rzeczą**, a zatem nie może być przedmiotem prawa własności.',
      '',
      '_(Więcej na ten temat: [„Czy jestem właścicielem swojego zwierzęcia?”](https://paragrafzwierzoochronny.pl/czy-jestem-wlascicielem-swojego-zwierzecia/), [„Własność znalezionego zwierzęcia”](https://paragrafzwierzoochronny.pl/wlasnosc-znalezionego-zwierzecia/))_',
    ],
  },
  {
    heading: 'Czym właściwie jest „umowa adopcyjna”?',
    paragraphs: [
      'Umowa adopcyjna zawierana ze schroniskiem to umowa nienazwana.',
      '',
      'Nienazwana — czyli taka, która nie została uregulowana bezpośrednio w kodeksie cywilnym.',
      '',
      'Dlatego nie ma „odgórnie”, tzn. ustawowo określonych cech, jakie musi ona spełniać.',
      '',
      'W praktyce, zgodnie z umową adopcyjną:',
    ],
    list: [
      'schronisko **nieodpłatnie przekazuje Ci zwierzę**,',
      'Ty zobowiązujesz się do **sprawowania pieczy nad zwierzęciem oraz do zapewnienia mu opieki i ochrony**.',
    ],
    listStyle: 'paw',
  },
  {
    heading: 'Co jeszcze zawiera umowa adopcyjna?',
    paragraphs: [
      'Umowa adopcyjna szczegółowo określa Twoje obowiązki wobec zwierzęcia, w tym:',
    ],
    list: [
      'zapewnienia mu **właściwego odżywiania**,',
      'dbania o jego **zdrowie i higienę**,',
      'zapewnienia **bezpieczeństwa i odpowiednich warunków życia**.',
    ],
    listStyle: 'paw',
  },
  {
    heading: '',
    paragraphs: ['Ponadto umowa adopcyjna:'],
    list: [
      'zakazuje **znęcania się nad zwierzęciem**,',
      'zakazuje **sprzedaży, darowania lub porzucenia** zwierzęcia.',
    ],
    listStyle: 'paw',
  },
  {
    heading: 'Ważne: Co masz zrobić, jeżeli nie możesz dalej sprawować opieki nad zwierzęciem?',
    paragraphs: [
      'Masz obowiązek zwrócić zwierzę do schroniska, z którym zawarłeś umowę adopcyjną.',
    ],
  },
  {
    heading: 'Umowa adopcyjna a… władza rodzicielska?',
    paragraphs: [
      'Choć brzmi to nietypowo, obowiązki wynikające z umowy adopcyjnej są zbliżone do tych, które znamy z **władzy rodzicielskiej** – która to władza obejmuje obowiązek sprawowania pieczy nad dzieckiem, poszanowania jego godności i praw oraz zakaz stosowania kar cielesnych wobec dziecka.',
    ],
  },
  {
    heading: 'Podobieństwo do umowy przechowania',
    paragraphs: [
      'Patrząc na umowę adopcyjną z innej perspektywy, znajdziemy w niej pewne elementy wspólne z **umową przechowania** (vide: art. 835 Kodeksu cywilnego).',
      '',
      'Adoptujący zwierzę, podobnie jak przechowawca, zobowiązuje się do zachowania zwierzęcia „w stanie niepogorszonym”.',
    ],
  },
  {
    heading: 'Potrzebujesz więcej informacji?',
    paragraphs: [
      'Jeżeli masz pytania odnośnie umów adopcyjnych zwierząt — napisz do mnie na **piotr.pienkowski@adwokatura.pl**.',
      '',
      'Z przyjemnością pomogę.',
      '',
      'Do następnego 😊',
    ],
  },
],
relatedSlugs: ['czy-jestem-wlascicielem'],
ctaTitle: 'Masz wątpliwości dotyczące umowy adopcyjnej?',
ctaText:
  'Jeżeli zastanawiasz się nad zawarciem umowy adopcyjnej albo wątpliwości interpretacyjne po jej zawarciu- skontaktuj się z nami.',
translations: {
  en: {
    title: 'What should you know about an adoption agreement?',
    teaser:
      'What does an animal adoption agreement include? Why does adoption not make you the “owner” of the animal? Here are the key points.',
    sections: [
      {
        heading: '',
        paragraphs: [
          'Adopting an animal from a shelter is a beautiful decision, but it is also a concrete commitment — including a legal one. An adoption agreement sets out what care for the animal should look like after it leaves the shelter.',
          '',
          'I encourage you to read this article to get familiar with the topic.',
        ],
      },
      {
        heading: 'Does adoption make you the “owner” of the animal?',
        paragraphs: [
          'No.',
          '',
          'And it is exactly the same if you buy an animal or find one.',
          '',
          'I will keep repeating this: **an animal is not a thing**, and therefore it cannot be the subject of a right of ownership.',
          '',
          '_(More on this (in Polish): [“Czy jestem właścicielem swojego zwierzęcia?”](https://paragrafzwierzoochronny.pl/czy-jestem-wlascicielem-swojego-zwierzecia/), [“Własność znalezionego zwierzęcia”](https://paragrafzwierzoochronny.pl/wlasnosc-znalezionego-zwierzecia/))_',
        ],
      },
      {
        heading: 'What exactly is an “adoption agreement”?',
        paragraphs: [
          'An adoption agreement concluded with a shelter is an innominate contract.',
          '',
          'Innominate — meaning a contract that is not regulated directly in the Civil Code.',
          '',
          'That is why there are no “top-down”, statutory features it must meet.',
          '',
          'In practice, under an adoption agreement:',
        ],
        list: [
          'the shelter **transfers the animal to you free of charge**,',
          'you undertake to **exercise custody over the animal and ensure its care and protection**.',
        ],
        listStyle: 'paw',
      },
      {
        heading: 'What else does an adoption agreement include?',
        paragraphs: [
          'The agreement specifies your obligations towards the animal in detail, including:',
        ],
        list: [
          'providing **proper nutrition**,',
          'taking care of its **health and hygiene**,',
          'ensuring **safety and appropriate living conditions**.',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: ['In addition, the adoption agreement:'],
        list: [
          'prohibits **animal abuse**,',
          'prohibits **selling, giving away, or abandoning** the animal.',
        ],
        listStyle: 'paw',
      },
      {
        heading: 'Important: what if you can no longer care for the animal?',
        paragraphs: [
          'You are obliged to return the animal to the shelter with which you signed the adoption agreement.',
        ],
      },
      {
        heading: 'An adoption agreement and… parental authority?',
        paragraphs: [
          'Although it may sound unusual, the obligations under an adoption agreement are similar to those we know from **parental authority** — which includes the duty to exercise custody over a child, respect the child’s dignity and rights, and a prohibition of corporal punishment.',
        ],
      },
      {
        heading: 'Similarity to a deposit agreement',
        paragraphs: [
          'Looking at an adoption agreement from another angle, we can find elements it shares with a **deposit agreement** (see Article 835 of the Polish Civil Code).',
          '',
          'A person adopting an animal, like a depositary, undertakes to keep the animal “in an undeteriorated condition”.',
        ],
      },
      {
        heading: 'Need more information?',
        paragraphs: [
          'If you have questions about animal adoption agreements, write to me at **piotr.pienkowski@adwokatura.pl**.',
          '',
          'I will be happy to help.',
          '',
          'See you next time 😊',
        ],
      },
    ],
    ctaTitle: 'Do you have doubts about an adoption agreement?',
    ctaText:
      'If you are considering entering into an adoption agreement or have interpretative doubts after signing it, feel free to contact us.',
  },
  es: {
    title: '¿Qué deberías saber sobre el contrato de adopción?',
    teaser:
      '¿Qué incluye un contrato de adopción? ¿Por qué adoptar no te convierte en “propietario” del animal? Aquí tienes los puntos clave.',
    sections: [
      {
        heading: '',
        paragraphs: [
          'Adoptar un animal de un refugio es una decisión preciosa, pero también un compromiso concreto — también jurídico. El contrato de adopción establece cómo debe ser el cuidado del animal una vez que sale del refugio.',
          '',
          'Te animo a leer este artículo para familiarizarte con el tema.',
        ],
      },
      {
        heading: '¿La adopción te convierte en “propietario” del animal?',
        paragraphs: [
          'No.',
          '',
          'Y ocurre exactamente lo mismo si compras un animal o si encuentras uno.',
          '',
          'Lo repetiré hasta la saciedad: **el animal no es una cosa**, por lo que no puede ser objeto del derecho de propiedad.',
          '',
          '_(Más sobre este tema (en polaco): [«Czy jestem właścicielem swojego zwierzęcia?»](https://paragrafzwierzoochronny.pl/czy-jestem-wlascicielem-swojego-zwierzecia/), [«Własność znalezionego zwierzęcia»](https://paragrafzwierzoochronny.pl/wlasnosc-znalezionego-zwierzecia/))_',
        ],
      },
      {
        heading: '¿Qué es exactamente un “contrato de adopción”?',
        paragraphs: [
          'El contrato de adopción firmado con un refugio es un contrato atípico.',
          '',
          'Atípico — es decir, no regulado directamente en el Código Civil.',
          '',
          'Por eso no existen características legales “prefijadas” que deba cumplir.',
          '',
          'En la práctica, según el contrato de adopción:',
        ],
        list: [
          'el refugio **te entrega el animal de forma gratuita**,',
          'tú te comprometes a **ejercer la custodia del animal y a garantizarle cuidado y protección**.',
        ],
        listStyle: 'paw',
      },
      {
        heading: '¿Qué más incluye el contrato de adopción?',
        paragraphs: [
          'El contrato detalla tus obligaciones hacia el animal, entre ellas:',
        ],
        list: [
          'proporcionarle una **alimentación adecuada**,',
          'cuidar de su **salud e higiene**,',
          'garantizar su **seguridad y unas condiciones de vida apropiadas**.',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: ['Además, el contrato de adopción:'],
        list: [
          'prohíbe el **maltrato animal**,',
          'prohíbe **vender, regalar o abandonar** al animal.',
        ],
        listStyle: 'paw',
      },
      {
        heading: 'Importante: ¿qué pasa si no puedes seguir cuidando del animal?',
        paragraphs: [
          'Tienes la obligación de devolver el animal al refugio con el que firmaste el contrato de adopción.',
        ],
      },
      {
        heading: '¿Contrato de adopción y… patria potestad?',
        paragraphs: [
          'Aunque suene poco habitual, las obligaciones del contrato de adopción se parecen a las que conocemos de la **patria potestad**: deber de custodia, respeto de la dignidad y derechos, y prohibición de castigos corporales.',
        ],
      },
      {
        heading: 'Semejanzas con el contrato de depósito',
        paragraphs: [
          'Si miramos el contrato de adopción desde otra perspectiva, encontramos elementos comunes con el **contrato de depósito** (véase el art. 835 del Código Civil polaco).',
          '',
          'La persona que adopta, al igual que el depositario, se compromete a conservar al animal “en estado no deteriorado”.',
        ],
      },
      {
        heading: '¿Necesitas más información?',
        paragraphs: [
          'Si tienes preguntas sobre contratos de adopción de animales, escríbeme a **piotr.pienkowski@adwokatura.pl**.',
          '',
          'Será un placer ayudarte.',
          '',
          'Hasta la próxima 😊',
        ],
      },
    ],
    ctaTitle: '¿Tienes dudas sobre un contrato de adopción?',
    ctaText:
      'Si estás considerando firmar un contrato de adopción o tienes dudas interpretativas después de haberlo firmado, ponte en contacto con nosotros.',
  },
},

  },
  {
    slug: 'czy-jestem-wlascicielem',
    title: 'Czy jestem właścicielem swojego zwierzęcia?',
    category:'Paragraf Zwierzoochronny',
    teaser: 'Nie jesteś właścicielem swojego zwierzęcia – niezależnie od tego, czy znalazłeś, kupiłeś, czy wziąłeś ze schroniska. Dowiedz się więcej w tym artykule.',
    date: '25 października 2025',
    readingTime: '2 min czytania',
    link: '/blog/czy-jestem-wlascicielem.html',
    featured: false,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/czy-jestem-wlascicielem.jpg',
      alt: 'Pies na spacerze - ilustracja do artykułu o własności zwierząt',
    },
    tags: [
      'czy mogę rozporządzać zwierzęciem',
      'czy mogę sprzedać zwierzę',
      'czy zwierzę to rzecz',
      'jak nabyć własność zwierzęcia',
      'kto jest właścicielem zwierzęcia',
      'własność kota',
      'własność kota ze schroniska',
      'własność psa',
    ],
    sections: [
      {
        heading: '',
        paragraphs: [
          'Zaskoczę Cię – **Nie jesteś właścicielem swojego zwierzęcia.**',
          'I to niezależnie od tego, czy zwierzę znalazłeś, kupiłeś, czy wziąłeś ze schroniska.',
          'Jak to możliwe?',
          'Przecież w ustawie o ochronie zwierząt z dnia 21 sierpnia 1997 roku (Dz.U. Nr 111, poz. 724 ze zm.) pojawia się pojęcie _„właściciel"_.',
          'A jednak — **z prawnego punktu widzenia, nie możesz być właścicielem zwierzęcia**.',
        ],
      },
      {
        heading: 'ZWIERZĘ NIE JEST RZECZĄ',
        paragraphs: [
          'Zgodnie z **art. 140 Kodeksu cywilnego** z dnia 23 kwietnia 1964 r. (Dz.U. Nr 16, poz. 93 ze zm.), prawo własności dotyczy tylko rzeczy.',
          'A — jak jasno stwierdza art. 1 ust. 1 ustawy o ochronie zwierząt —',
          '"_**Zwierzę**, jako istota żyjąca, zdolna do odczuwania cierpienia, **nie jest rzeczą**._"',
          'Nie można więc być właścicielem zwierzęcia w taki sam sposób, jak jest się właścicielem samochodu, czy telefonu.',
        ],
      },
      {
        heading: 'CZY TO ZNACZY, ŻE NIE MASZ ŻADNYCH PRAW DO SWOJEGO ZWIERZĘCIA?',
        paragraphs: [
          'Możesz mieć prawo podmiotowe do zwierzęcia, podobne do prawa własności.',
          'Prawo to należy wywodzić z art. 1 ust. 2 ustawy o ochronie zwierząt, który stanowi, że:',
          '"_Do zwierząt stosuje się odpowiednio przepisy dotyczące rzeczy._"',
          'Takie prawo podmiotowe obejmuje prawo do decydowania o zwierzęciu (np. o jego sprzedaży).',
        ],
      },
      {
        heading: 'NIE TYLKO PRAWA, ALE I OBOWIĄZKI',
        paragraphs: [
          'Wykonywanie praw do zwierzęcia musi iść w parze z obowiązkiem, m.in.:',
        ],
        list: [
          'poszanowania jego godności,',
          'zapewnienia mu opieki i bezpieczeństwa,',
          'humanitarnego traktowania.',
        ],
        listStyle: 'paw',
      },
      {
        heading: 'PODSUMOWANIE',
        paragraphs: [
          'Nie jesteś właścicielem swojego zwierzęcia.',
          'Możesz mieć prawo podobne do własności zwierzęcia, z którym wiążą się liczne obowiązki.',
          'Obowiązki, o których powinieneś pomyśleć w pierwszej kolejności.',
          'Do następnego. 🙂',
        ],
      },
    ],
    relatedSlugs: ['umowa-adopcyjna'],
    ctaTitle: 'Masz spór o prawo „własności” zwierzęcia?',
    ctaText:
      'Powstał spór, kto jest "właścicielem" zwierzęcia – po rozstaniu, adopcji albo znalezieniu zwierzęcia? Skontaktuj się z nami.',
    translations: {
      en: {
        title: 'Am I the “owner” of my animal?',
        teaser:
          'You are not the owner of your animal – whether you found it, bought it or adopted it from a shelter. Learn what “ownership” of an animal really means in law.',
        sections: [
          {
            heading: '',
            paragraphs: [
              'It may surprise you, but **you are not the owner of your animal.**',
              'This is true regardless of whether you found the animal, bought it or adopted it from a shelter.',
              'How is that possible?',
              'After all, the Polish Animal Protection Act of 21 August 1997 uses the term “owner”.',
              'And yet **from a legal point of view you cannot be the owner of an animal in the same way as you are the owner of a thing.**',
            ],
          },
          {
            heading: 'An animal is not a thing',
            paragraphs: [
              'Under **Article 140 of the Polish Civil Code** of 23 April 1964, the right of ownership applies only to things.',
              'And – as Article 1(1) of the Animal Protection Act clearly states –',
              '"_**An animal**, as a living being capable of suffering, **is not a thing**._"',
              'You therefore cannot be the owner of an animal in the same way as you are the owner of a car or a phone.',
            ],
          },
          {
            heading: 'Does that mean you have no rights in relation to your animal?',
            paragraphs: [
              'You can have a subjective right to an animal that is similar to ownership.',
              'This right is derived from Article 1(2) of the Animal Protection Act, which provides that the provisions on things apply to animals **accordingly**, not directly.',
              'Such a right includes, among other things, the ability to decide about the animal – for example to sell it or entrust it to someone else – always within the limits set by animal protection law.',
            ],
          },
          {
            heading: 'Not only rights, but also duties',
            paragraphs: [
              'Exercising your rights in relation to an animal must go hand in hand with duties, including:',
            ],
            list: ['respecting its dignity,', 'ensuring its care and safety,', 'treating it humanely.'],
            listStyle: 'paw',
          },
          {
            heading: 'Summary',
            paragraphs: [
              'You are not the owner of your animal in the strict legal sense.',
              'You can have a right similar to ownership, and it comes with a number of duties.',
              'It is these duties – respect, care and protection – that should come first.',
              'Till next time. 🙂',
            ],
          },
        ],
        ctaTitle: 'Dispute over “ownership” of an animal?',
        ctaText:
          'If there is any dispute about who is the "owner" of an animal, after - the break‑up, adoption or finding – please contact us.',
      },
      es: {
        title: '¿Soy el “propietario” de mi animal?',
        teaser:
          'No eres el propietario de tu animal – tanto si lo has encontrado como si lo has comprado o adoptado en un refugio. Descubre qué significa realmente la “propiedad” de un animal en derecho.',
        sections: [
          {
            heading: '',
            paragraphs: [
              'Puede que te sorprenda, pero **no eres el propietario de tu animal.**',
              'Y esto con independencia de que lo hayas encontrado, lo hayas comprado o lo hayas adoptado en un refugio.',
              '¿Cómo es posible?',
              'La Ley polaca de Protección de los Animales de 21 de agosto de 1997 utiliza el término “propietario”.',
              'Sin embargo, **desde el punto de vista jurídico no puedes ser propietario de un animal en el mismo sentido que lo eres de una cosa.**',
            ],
          },
          {
            heading: 'El animal no es una cosa',
            paragraphs: [
              'Conforme al **artículo 140 del Código Civil polaco** de 23 de abril de 1964, el derecho de propiedad solo se refiere a las cosas.',
              'Y, como establece claramente el artículo 1.1 de la Ley de Protección de los Animales,',
              '"_**El animal**, como ser vivo capaz de sufrir, **no es una cosa**._"',
              'Por tanto, no puedes ser propietario de un animal del mismo modo que lo eres de un coche o de un teléfono.',
            ],
          },
          {
            heading: '¿Significa eso que no tienes derechos sobre tu animal?',
            paragraphs: [
              'Puedes tener un derecho subjetivo sobre el animal similar al derecho de propiedad.',
              'Este derecho se deriva del artículo 1.2 de la Ley de Protección de los Animales, que dispone que a los animales se aplican las normas sobre cosas **de forma correspondiente**, no directa.',
              'Ese derecho abarca, entre otras cosas, la posibilidad de decidir sobre el animal –por ejemplo venderlo o confiarlo al cuidado de otra persona– siempre dentro de los límites marcados por las normas de protección de los animales.',
            ],
          },
          {
            heading: 'No solo derechos, también obligaciones',
            paragraphs: [
              'El ejercicio de tus derechos respecto al animal debe ir acompañado de obligaciones, entre ellas:',
            ],
            list: ['respetar su dignidad,', 'garantizarle cuidados y seguridad,', 'tratarlo de forma humana.'],
            listStyle: 'paw',
          },
          {
            heading: 'Resumen',
            paragraphs: [
              'En sentido estrictamente jurídico, no eres el propietario de tu animal.',
              'Puedes tener un derecho similar a la propiedad, ligado a numerosas obligaciones.',
              'Son precisamente esas obligaciones –respeto, cuidados y protección– las que deben estar en primer plano.',
              'Hasta la próxima. 🙂',
            ],
          },
        ],
        ctaTitle: '¿Tienes un conflicto sobre la “propiedad” de un animal?',
        ctaText:
          'Si ha surgido un conflicto sobre quién tiene derecho a decidir sobre un animal –tras una ruptura, una adopción o haberlo encontrado– conéctate con nosotros.',
      },
    },
  },
  {
    slug: 'odszkodowanie-za-pogryzione-zwierze',
    title: 'Odszkodowanie za pogryzione zwierzę',
    category: 'Paragraf Zwierzoochronny',
    teaser: 'Przeczytaj ten artykuł, a dowiesz się, jak kroki prawne podjąć, żeby uzyskać odszkodowanie za pogryzione zwierzę.',
    date: '18 maja 2025',
    readingTime: '2 min czytania',
    link: '/blog/odszkodowanie-za-pogryzione-zwierze.html',
    featured: false,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/odszkodowanie-za-pogryzione-zwierze.jpg',
      alt: 'Pies – ilustracja do artykułu o odszkodowaniu za pogryzione zwierzę',
    },
    tags: [
      'kto jest odpowiedzialny za zwierzę',
      'kto mi zapłaci za pogryzienie',
      'kto płaci za szkodę spowodowaną przez psa',
      'odszkodowanie za pogryzionego kota',
      'odszkodowanie za pogryzienie przez dzikie zwierzę',
      'odszkodowanie za pogryzienie przez psa',
      'odszkodowanie za pogryzione zwierzę',
      'odszkodowanie za pogryzionego psa',
    ],
    sections: [
      {
        heading: 'MÓJ PUPIL ZOSTAŁ POGRYZIONY - CO MAM ROBIĆ?',
        paragraphs: [
          'Na wstępie mam pytanie.',
          'Co pogryzło Twoje zwierzę?',
        ],
      },
      {
        heading: '',
        paragraphs: [
          'Jeśli do pogryzienia doszło przez **zwierzę żyjące w stanie wolnym**, odpowiedzialność może ponosić **Skarb Państwa** (vide: ustawa prawo łowieckie z dnia 13 października 1995 roku, Dz.U. Nr 147, poz. 713 ze zm., ustawy o ochronie przyrody z dnia 16 kwietnia 2004 roku, Dz.U. Nr 92, poz. 880 ze zm. oraz ustawa kodeks cywilny z dnia 23 kwietnia 1964 roku, Dz.U. Nr 16, poz. 93 ze zm.).',
          '',
          'Jeżeli Twojego pupila pogryzło zwierzę chowane przez człowieka, to odpowiedzialny jest ten, kto zwierzę chowa albo się nim posługuje (vide: art. 431 ustawy z dnia 23 kwietnia 1964 roku kodeks cywilny, Dz.U. Nr 16, poz. 93 ze zm.).',
          '',
          'Ten, kto zwierzę chowa, czyli przygarnął je, karmi, leczy, ma zamiar trwałego sprawowania opieki nad zwierzęciem.',
          '',
          'Ten, kto się nim posługuje, czyli wykorzystuje je okazjonalnie, dorywczo bądź jednorazowo.',
          '',
          'Ww. osoba chowająca albo posługująca się zwierzęciem jest ustalana na moment wyrządzenia szkody.',
          '',
          'Tytuł prawny do zwierzęcia pozostaje tu bez znaczenia.',
        ],
      },
      {
        heading: 'JAK UZYSKAĆ ODSZKODOWANIE?',
        paragraphs: [
          'Żeby dostać odszkodowanie, musisz udowodnić:',
        ],
        list: [
          'chowanie zwierzęcia albo posługiwanie się zwierzęciem przez podmiot będący adresatem roszczenia odszkodowawczego,',
          'wyrządzenie szkody (oraz jej wysokość),',
          'adekwatny związek przyczynowy pomiędzy ww. szkodą, a pogryzieniem Twojego pupila.',
        ],
        listStyle: 'paw',
      },
      {
        heading: 'WINA W NADZORZE NAD ZWIERZĘCIEM',
        paragraphs: [
          'Osoba, przeciwko której dochodzisz odszkodowania, może bronić się w ten sposób, że nie ponosi winy w nadzorze nad zwierzęciem.',
          '',
          'Nie wystarczy jednak wykazanie przez tę osobę, że zwierzę nie znajdowało się pod jej nadzorem w chwili pogryzienia.',
          '',
          'Ww. osoba jest odpowiedzialna za zwierzę, nawet, jeżeli zbłąkało się albo uciekło.',
          '',
          'Natomiast odpowiedzialność ww. osoby jest wyłączona, przykładowo, jeżeli zwierzę zostało skradzione.',
        ],
      },
      {
        heading: 'ILE MOŻESZ OTRZYMAĆ ODSZKODOWANIA ZA POGRYZIONE ZWIERZĘ?',
        paragraphs: [
          'Tyle, ile wydałeś na zwierzę w związku z jego pogryzieniem (w tym na leczenie, wzrost kosztów utrzymania pupila – musisz to wykazać przed sądem).',
          '',
          'Odszkodowanie nie obejmuje strat moralnych. Do tego służy zadośćuczynienie. O zadośćuczynieniu czytaj [tutaj](https://paragrafzwierzoochronny.pl/zadoscuczynienie-za-pogryzione-zwierze/).',
          '',
          'W odrębnym artykule zajmę się przypadkiem pogryzienia osoby przez zwierzę.',
          '',
          'Nadmieniam, że roszczenie odszkodowawcze w związku z pogryzieniem przez zwierzę może również zostać zgłoszone w postępowaniu karnym.',
          '',
          'Do następnego. 🙂',
        ],
      },
    ],
    relatedSlugs: ['umowa-adopcyjna', 'czy-jestem-wlascicielem'],
    ctaTitle: 'Twoje zwierzę zostało pogryzione?',
    ctaText:
      'Możesz dochodzić zwrotu kosztów leczenia i innych wydatków- skontaktuj się z nami, by dowiedzieć się więcej na temat poruszony w artykule.',
    translations: {
      en: {
        title: 'Compensation for an injured animal – what can you claim?',
        teaser:
          'Learn what steps you can take to obtain compensation when your animal has been bitten or attacked.',
        sections: [
          {
            heading: 'My pet was bitten – what should I do?',
            paragraphs: [
              'First of all, ask yourself one question:',
              '',
              '**What animal bit your pet?**',
              '',
              'The answer determines who may be liable for the damage and from whom you can seek compensation.',
            ],
          },
          {
            heading: 'Wild animal or animal kept by a person?',
            paragraphs: [
              'If your pet was bitten by a **wild animal**, liability may fall on the **State Treasury** – under hunting law, nature protection law and civil law provisions.',
              '',
              'If your animal was bitten by an animal kept by a person, the person liable is the one who keeps the animal or makes use of it (Article 431 of the Polish Civil Code).',
              '',
              'A person who *keeps* an animal is someone who has taken it in, feeds it, treats it and intends to exercise permanent care over it.',
              '',
              'A person who *uses* an animal is someone who uses it occasionally, temporarily or on a one‑off basis.',
              '',
              'This person is identified **at the time when the damage occurs**, and the formal title to the animal is not decisive on its own.',
            ],
          },
          {
            heading: 'What do you need to prove to obtain compensation?',
            paragraphs: [
              'In order to obtain compensation, you must prove:',
            ],
            list: [
              'that the person you are claiming from kept the animal or made use of it,',
              'that damage occurred (and its amount),',
              'a causal link between the bite and the damage caused.',
            ],
          },
          {
            heading: 'Fault in supervising the animal',
            paragraphs: [
              'The person against whom you pursue compensation may argue that they are not at fault in supervising the animal.',
              '',
              'However, it is **not enough** for them to show that the animal was not physically under their supervision at the moment of the bite.',
              '',
              'As a rule they remain liable even if the animal strayed or escaped; liability may be excluded only in exceptional situations, for example if the animal has been stolen.',
            ],
          },
          {
            heading: 'How much can you get?',
            paragraphs: [
              'As compensation you can claim what you have spent on the animal as a result of the incident – in particular:',
            ],
            list: [
              'veterinary treatment costs,',
              'increased costs of care and maintenance (you will have to prove these before the court).',
            ],
          },
          {
            heading: 'Compensation vs. non‑material damage',
            paragraphs: [
              'Compensation does **not** cover moral damage and emotional suffering – for that you may seek **non‑material damages (zadośćuczynienie)**.',
              '',
              'I discuss this in a separate article on non‑material damages for an injured animal.',
              '',
              'You can also raise your compensation claim in **criminal proceedings** related to the incident.',
            ],
          },
        ],
         ctaTitle: 'Has your animal been bitten?',
    ctaText:
      'You may seek reimbursement of veterinary treatment costs and other expenses – contact us to learn more about the issues discussed in this article.',
      },
      es: {
        title: 'Indemnización por un animal mordido – ¿qué puedes reclamar?',
        teaser:
          'Descubre qué pasos puedes dar para obtener una indemnización cuando tu animal ha sido mordido o atacado.',
        sections: [
          {
            heading: 'Han mordido a mi animal – ¿qué hago?',
            paragraphs: [
              'En primer lugar, plantéate una pregunta:',
              '',
              '**¿Qué animal ha mordido a tu compañero?**',
              '',
              'La respuesta determina quién puede ser responsable del daño y a quién puedes reclamar.',
            ],
          },
          {
            heading: '¿Animal salvaje o animal mantenido por una persona?',
            paragraphs: [
              'Si el ataque lo ha causado un **animal que vive en estado salvaje**, la responsabilidad puede recaer en el **Estado** – en virtud de la ley de caza, la ley de protección de la naturaleza y las normas civiles.',
              '',
              'Si a tu animal lo ha mordido un animal mantenido por una persona, la persona responsable es quien lo **cría** o **se sirve de él** (art. 431 del Código Civil polaco).',
              '',
              'Quien *cría* al animal es la persona que lo ha acogido, lo alimenta, lo trata y tiene la intención de ejercer una custodia permanente.',
              '',
              'Quien *se sirve* del animal es la persona que lo utiliza de forma ocasional, temporal o puntual.',
              '',
              'La persona responsable se determina **en el momento de producirse el daño**, y el título jurídico formal sobre el animal no es decisivo por sí solo.',
            ],
          },
          {
            heading: 'Qué hay que probar para obtener una indemnización',
            paragraphs: [
              'Para obtener una indemnización tendrás que demostrar:',
            ],
            list: [
              'que la persona demandada criaba o se servía del animal,',
              'que se ha producido un daño (y cuál es su cuantía),',
              'la relación de causalidad entre la mordedura y el daño sufrido.',
            ],
          },
          {
            heading: 'Culpa en la vigilancia del animal',
            paragraphs: [
              'La persona frente a la que reclamas puede defenderse alegando que no tiene culpa en la vigilancia del animal.',
              '',
              'Sin embargo, **no basta** con demostrar que el animal no estaba físicamente bajo su supervisión en el momento del ataque.',
              '',
              'Por regla general sigue siendo responsable aunque el animal se haya escapado o se haya extraviado; la responsabilidad puede quedar excluida solo en supuestos excepcionales, por ejemplo cuando el animal ha sido robado.',
            ],
          },
          {
            heading: 'Cuánto puedes recibir',
            paragraphs: [
              'En concepto de indemnización puedes reclamar los gastos realizados en relación con el animal, en particular:',
            ],
            list: [
              'costes del tratamiento veterinario,',
              'aumento de los costes de cuidado y mantenimiento (que tendrás que acreditar ante el tribunal).',
            ],
          },
          {
            heading: 'Indemnización y daño moral',
            paragraphs: [
              'La indemnización **no** cubre el sufrimiento moral ni el daño emocional; para ello existe el **zadośćuczynienie** – una compensación por daño moral.',
              '',
              'Analizo esta cuestión en otro artículo dedicado a la compensación por sufrimiento relacionado con un animal.',
              '',
              'También puedes plantear tu reclamación de indemnización en el marco de un **procedimiento penal** relacionado con el hecho.',
            ],
          },
        ],
        ctaTitle: '¿Tu animal ha sido mordido?',
    ctaText:
      'Puedes reclamar el reembolso de los gastos de tratamiento veterinario y otros costes. Ponte en contacto con nosotros para saber más sobre las cuestiones tratadas en el artículo.',
      },
    },
  },
  {
    slug: 'zadoscuczynienie-za-pogryzione-zwierze',
    title: 'Zadośćuczynienie za pogryzione zwierzę',
    category: 'Paragraf Zwierzoochronny',
    teaser: 'Twoje zwierzę zostało pogryzione? Cierpisz z tego powodu? Zobacz, jak możesz uzyskać zadośćuczynienie za pogryzione zwierzę.',
    date: '8 lipca 2025',
    readingTime: '3 min czytania',
    link: '/blog/zadoscuczynienie-za-pogryzione-zwierze.html',
    featured: false,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/zadoscuczynienie-za-pogryzione-zwierze.jpg',
      alt: 'Pies w gabinecie weterynaryjnym po pogryzeniu',
    },
    tags: [
      'cierpienie z powodu utraty zwierzęcia',
      'cierpienie z powodu cierpienia zwierzęcia',
      'czy cierpienie zwierzęcia to dobro osobiste',
      'pieniądze za cierpienie psychiczne związane ze stratą psa',
      'pieniądze za cierpienie związane z pogryzieniem psa',
      'pogryzienie psa',
      'strata psa',
      'zadośćuczynienie za cierpienie psychiczne po utracie psa',
      'zadośćuczynienie za pogryzienie psa',
      'zadośćuczynienie za straty moralne',
    ],
    sections: [
      {
        heading: 'WSTĘP',
        paragraphs: [
          'Zanim opowiem Ci, jak uzyskać **zadośćuczynienie za pogryzione zwierzę**, wyjaśnijmy różnicę między **odszkodowaniem** a **zadośćuczynieniem**.',
          '',
          '**Odszkodowanie to** rekomopensata za poniesioną szkodę, w ramach której możesz dostać zwrot wydatków poniesionych w związku z pogryzieniem (na leczenie, utrzymanie – więcej o odszkodowaniu znajdziesz [tutaj](https://paragrafzwierzoochronny.pl/odszkodowanie-za-pogryzione-zwierze/)),',
          '',
          '**Zadośćuczynienie to** rekompensata za **krzywdę psychiczną**, którą odczuwasz w związku z pogryzieniem pupila.',
          '',
          'Zadośćuczynienie może zostać przyznane za **naruszenie dóbr osobistych**.',
          '',
          'Czy więź człowieka ze zwierzęciem jest dobrem osobistym?',
          '',
          'Prawo nie mówi tego wprost.',
          '',
          'Ale katalog dóbr osobistych, zawarty w art. 23 ustawy kodeks cywilny z dnia 23 kwietnia 1964 roku, Dz.U. Nr 16, poz. 93 ze zm., jest otwarty.',
          '',
          'Oznacza to, że dobrem osobistym może być wartość niewskazana w ww. przepisie.',
          '',
          'Moim zdaniem – i zdaniem części orzecznictwa – **więź emocjonalna między człowiekiem a zwierzęciem** zasługuje na ochronę prawną.',
        ],
      },
      {
        heading: 'ORZECZNICTWO',
        paragraphs: [
          'W uzasadnieniu wyroku z dnia 7 września 2017 roku, wydanego w sprawie o sygn. akt: II Ca 1111/17, Sąd Okręgowy w Krakowie wskazał, że:',
          '',
          '_"Pies uważany jest w społeczeństwie za zwierzę szczególne, które towarzyszy człowiekowi. W potocznym odbiorze pies jest symbolem wierności i bezinteresownej przyjaźni, a lojalność i oddanie, jakie okazuje człowiekowi, stanowią część jego naturalnego instynktu, ściśle związanego z ludzkim poczuciem miłości i przyjaźni. Dlatego nie sposób kwestionować tego, że pomiędzy człowiekiem i psem mogą wytworzyć się szczególne relacje, oparte na wzajemnym przywiązaniu._',
          '',
          '_Nie sposób kwestionować tego, że za dobro osobiste człowieka, podlegające ochronie na gruncie prawa cywilnego, może zostać uznana jego więź ze zwierzęciem, w szczególności z psem."_',
          '',
          'W ww. sprawie Sąd stwierdził, że **więź ze zwierzęciem może być uznana za dobro osobiste** i przyznał **zadośćuczynienie w wysokości 5 300 zł** za cierpienie po śmierci psa pogryzionego przez inne zwierzę.',
          '',
          'Nie wszystkie sądy są jednak zgodne w definiowaniu dóbr osobistych. Przykład?',
          '',
          'Wyrok Sądu Rejonowego w Olkuszu z dnia 28 maja 2021 roku (sygn. akt: I C 105/20), w którym uznano, że taka więź nie stanowi dobra osobistego.',
          '',
          'Mimo rozbieżności w orzecznictwie, uważam, że istnieją realne podstawy, by dochodzić **zadośćuczynienia za pogryzienie psa lub innego pupila**.',
        ],
      },
      {
        heading: 'Kto zapłaci zadośćuczynienie?',
        paragraphs: [
          'Zadośćuczynienie może zapłacić:',
        ],
        list: [
          'osoba chowająca zwierzę (które pogryzło naszego pupila),',
          'osoba posługująca się ww. zwierzęciem (więcej na temat osoby chowającej lub posługującej się zwierzęciem znajdziesz w moim [artykule](/blog/odszkodowanie-za-pogryzione-zwierze.html) dotyczącym odszkodowania za pogryzienie),',
          'Skarb Państwa – w przypadku dzikich zwierząt.',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: [
          'Roszczenie o zadośćuczynienie za pogryzione zwierzę może również zostać zgłoszone w postępowaniu karnym.',
          '',
          'Jeśli Twój pies lub inne zwierzę zostało pogryzione i cierpisz z tego powodu – **masz prawo walczyć o zadośćuczynienie**.',
          '',
          'Nie rezygnuj – każda sprawa jest inna, a precedensy pokazują, że **można wygrać**.',
          '',
          'Do następnego. 🙂',
        ],
      },
    ],
    relatedSlugs: ['odszkodowanie-za-pogryzione-zwierze', 'czy-jestem-wlascicielem'],
    ctaTitle: 'Myślisz o zadośćuczynieniu za pogryzione zwierzę?',
    ctaText:
      'Jeżeli cierpisz po pogryzieniu lub śmierci zwierzęcia i zastanawiasz się nad zadośćuczynieniem - skontaktuj się z nami, przeanalizujemy Twoją sytuację, oszacujemy wysokość zadośćuczynienia i pomożemy Tobie podjąć odpowiednie kroki prawne.',
   translations: {
  en: {
    title: 'Non-material damages for an injured animal – is it possible?',
    teaser:
      'Your animal has been bitten and you suffer because of it? See when you can claim non-material damages for the violation of your personal rights.',
    sections: [
      {
        heading: 'Compensation vs. non-material damages',
        paragraphs: [
          'Before discussing non-material damages for an injured animal, it is worth understanding the difference between **compensation** and **non-material damages**.',
          '',
          '**Compensation** is reimbursement for financial loss – for example, the cost of treatment or increased care (I discuss this in a separate article on compensation for an injured animal).',
          '',
          '**Non-material damages** are compensation for **psychological suffering** – the harm you experience because your animal has been injured or killed.',
          '',
          'Non-material damages may be awarded where **personal rights** have been violated.',
          '',
          'The question is: can the bond between a person and their animal be treated as a personal right?',
        ],
      },
      {
        heading: 'Case law on the bond with an animal',
        paragraphs: [
          'In a judgment of 7 September 2017 (II Ca 1111/17), the District Court in Kraków held that the bond between a person and a dog can constitute a personal right protected by law.',
          '',
          'The court noted that dogs are often perceived as companions, and the emotional relationship with them is connected with human feelings of attachment, friendship and loyalty.',
          '',
          'In that case the court awarded non-material damages of PLN 5,300 for suffering after the death of a dog killed by another animal.',
          '',
          'Not all courts share this view – there are decisions in which such a bond is not recognised as a personal right. However, the jurisprudence shows that it is **possible** to obtain non-material damages for the suffering caused by an attack on an animal.',
        ],
      },
      {
        heading: 'Who may have to pay non-material damages?',
        paragraphs: ['Non-material damages may be paid by:'],
        list: [
          'the person who keeps the animal that caused the attack,',
          'the person who uses that animal (I explain this in more detail in the article on compensation for an injured animal),',
          'the State Treasury – in cases involving wild animals.',
        ],
      },
      {
        heading: 'How to pursue the claim',
        paragraphs: [
          'A claim for non-material damages can be pursued in civil proceedings and, in some situations, also within criminal proceedings as an ancillary claim.',
          '',
          'Each case is different – among other things, the court will assess the strength of the bond, the circumstances of the attack and the behaviour of the defendant.',
        ],
      },
      {
        heading: '',
        paragraphs: [
          'A claim for non-material damages for an animal that has been bitten may also be brought in criminal proceedings.',
          '',
          'If your dog or other animal has been bitten and you suffer because of it, you **have the right to seek non-material damages**.',
          '',
          'Do not give up – every case is different, and precedents show that **it is possible to win**.',
          '',
          'Till next time. 🙂',
        ],
      },
    ],
    ctaTitle: 'Are you considering non-material damages for an animal that has been bitten?',
    ctaText:
      'If you are suffering after an animal has been bitten or has died and you are considering non-material damages, contact us. We will review your situation, estimate the potential amount, and help you take the appropriate legal steps.',
  },

  es: {
    title: 'Indemnización por daño moral por un animal mordido – ¿es posible?',
    teaser:
      '¿Tu animal ha sido mordido y sufres por ello? Descubre cuándo puedes reclamar una compensación por daño moral por la vulneración de tus derechos de la personalidad.',
    sections: [
      {
        heading: 'Indemnización y daño moral',
        paragraphs: [
          'Antes de hablar de la compensación por daño moral en caso de un animal mordido, conviene comprender la diferencia entre **indemnización** y **daño moral** (zadośćuczynienie).',
          '',
          '**La indemnización** cubre la pérdida económica – por ejemplo, costes de tratamiento o aumento de gastos de cuidado (trato esta cuestión en un artículo aparte sobre la indemnización por un animal mordido).',
          '',
          '**El daño moral** (zadośćuczynienie) se refiere a la **aflicción psicológica**, es decir, al sufrimiento que experimentas a raíz de las lesiones o la muerte de tu animal.',
          '',
          'El daño moral puede concederse cuando se ha producido una **vulneración de derechos de la personalidad**.',
          '',
          'La pregunta es: ¿puede la relación entre una persona y su animal considerarse un derecho de la personalidad?',
        ],
      },
      {
        heading: 'Jurisprudencia sobre el vínculo con un animal',
        paragraphs: [
          'En una sentencia de 7 de septiembre de 2017 (II Ca 1111/17), el Tribunal de Distrito de Cracovia consideró que el vínculo entre una persona y su perro puede constituir un derecho de la personalidad protegido por la ley.',
          '',
          'El tribunal señaló que el perro suele percibirse como un compañero, y que la relación emocional con él está vinculada a sentimientos humanos de apego, amistad y lealtad.',
          '',
          'En ese caso se concedió una compensación de 5.300 PLN por el sufrimiento causado por la muerte de un perro atacado por otro animal.',
          '',
          'No todos los tribunales comparten este enfoque: existen resoluciones en las que dicho vínculo no se reconoce como derecho de la personalidad. Aun así, la jurisprudencia muestra que **es posible** obtener una compensación por daño moral por el sufrimiento causado por un ataque a un animal.',
        ],
      },
      {
        heading: 'Quién puede pagar el daño moral',
        paragraphs: ['El daño moral puede ser abonado por:'],
        list: [
          'la persona que tiene bajo su cuidado al animal que atacó,',
          'la persona que se sirve de ese animal (explico esta figura con más detalle en el artículo sobre indemnización por un animal mordido),',
          'el Estado – en el caso de animales salvajes.',
        ],
      },
      {
        heading: 'Cómo hacer valer la reclamación',
        paragraphs: [
          'La reclamación por daño moral puede plantearse en un procedimiento civil y, en determinadas circunstancias, también en el marco de un procedimiento penal como demanda accesoria.',
          '',
          'Cada caso es distinto: el tribunal valorará, entre otras cosas, la intensidad del vínculo, las circunstancias del ataque y la conducta de la parte demandada.',
        ],
      },
      {
        heading: '',
        paragraphs: [
          'La reclamación por daño moral por un animal mordido también puede presentarse en un procedimiento penal.',
          '',
          'Si tu perro u otro animal ha sido mordido y sufres por ello, **tienes derecho a luchar por una compensación por daño moral**.',
          '',
          'No te rindas: cada caso es distinto, y los precedentes muestran que **se puede ganar**.',
          '',
          'Hasta la próxima. 🙂',
        ],
      },
    ],
    ctaTitle: '¿Estás pensando en una indemnización por daño moral por un animal mordido?',
    ctaText:
      'Si estás sufriendo tras la mordedura o la muerte de un animal y estás considerando reclamar una indemnización por daño moral, ponte en contacto con nosotros. Analizaremos tu situación, estimaremos el importe posible y te ayudaremos a dar los pasos legales adecuados.',
  },
},
 },
  {
    slug: 'jak-posiadac-niebezpieczne-zwierze',
    title: 'Chcę mieć niebezpieczne zwierzę!',
    category: 'Paragraf Zwierzoochronny',
    teaser: 'Zastanawiasz się, jak legalnie posiadać niebezpieczne zwierzę? Sprawdź, jak uzyskać stosowne zezwolenie.',
    date: '4 lipca 2025',
    readingTime: '4 min czytania',
    link: '/blog/jak-posiadac-niebezpieczne-zwierze.html',
    featured: false,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/niebezpieczne-zwierze.jpg',
      alt: 'Węże i inne egzotyczne zwierzęta - ilustracja do artykułu o niebezpiecznych zwierzętach',
    },
    tags: [
      'czy mogę mieć dzika',
      'czy mogę mieć dzikie zwierzę',
      'czy mogę mieć pająka',
      'czy mogę mieć papugę',
      'czy mogę mieć tarantulę',
      'czy mogę mieć węża',
      'do kogo zgoda na niebezpieczne zwierzę',
      'odstępstwo od zakazu posiadania niebezpiecznego zwierzęcia',
      'zezwolenie na niebezpieczne zwierze',
      'zezwolenie RDOŚ',
    ],
    sections: [
      {
        heading: 'Zwierzę bezpieczne czy niebezpieczne?',
        paragraphs: [
          'Zastanawiasz się, jak legalnie posiadać niebezpieczne zwierzę?',
          '',
          'Kluczowe jest ustalenie, czy wybrane przez Ciebie zwierzę w ogóle podlega pod kategorię „niebezpiecznego".',
          '',
          '**Sprawdź załącznik nr 1 oraz nr 2 do Rozporządzenia Ministra Środowiska z dnia 3 sierpnia 2011 roku** w sprawie gatunków zwierząt niebezpiecznych dla życia i zdrowia ludzi, Dz.U. 2011 nr 173 poz. 1037 ze zm.',
          '',
          '**Jeśli Twój wybraniec nie znajduje się w tych załącznikach**, nie oznacza to jeszcze, że możesz go trzymać bez żadnych ograniczeń. Może np. być objęty **ochroną gatunkową** – o czym napiszę w osobnym artykule.',
        ],
      },
      {
        heading: 'Twój wybraniec to rzeczywiście zwierzę niebezpieczne – co dalej?',
        paragraphs: [
          'Zgodnie z **art. 73 ust. 1 ustawy o ochronie przyrody** z dnia 16 kwietnia 2004 roku, Dz.U. Nr 92, poz. 880 ze zm., zabrania się:',
          '',
          '**1)** posiadania i przetrzymywania,',
          '**2)** sprowadzania z zagranicy,',
          '**3)** sprzedaży, wymiany, wynajmu, darowizny i użyczania podmiotom nieuprawnionym do ich posiadania',
          '',
          '– żywych zwierząt gatunków niebezpiecznych dla życia i zdrowia ludzi (zakaz dotyczy zwierząt wymienionych **w obu załącznikach** do rozporządzenia).',
        ],
      },
      {
        heading: 'Uzyskaj specjalne zezwolenie',
        paragraphs: [
          'Jeśli Twój wybraniec znajduje się **w ww. załączniku nr 2** (czyli należy do **II kategorii zwierząt niebezpiecznych**), złóż **wniosek o zezwolenie na odstępstwo od zakazu** jego posiadania jako niebezpiecznego.',
          '',
          'Wniosek ten należy złożyć do **Regionalnego Dyrektora Ochrony Środowiska (RDOŚ)** właściwego dla miejsca przetrzymywania zwierzęcia.',
          '',
          'Jeśli Twój wybraniec znajduje się w **załączniku nr 1**, (czyli należy do **I kategorii zwierząt niebezpiecznych**), teoretycznie nie ma możliwości uzyskania ww. zezwolenia (vide: art. 73 ust. 4 ww. ustawy o ochronie przyrody).',
          '',
          'Ale… **i tak warto spróbować** złożyć ww. wniosek.',
          '',
          'Jak **RDOŚ** odmówi udzielenia zezwolenia, to możesz odwołać się do **Generalnego Dyrektora Ochrony Środowiska**.',
          '',
          'Następnie iść do **Wojewódzkiego Sądu Administracyjnego**, a w końcu do **Naczelnego Sądu Administracyjnego**.',
          '',
          'Posłuchaj jednej historii, która potwierdza, że warto złożyć ww. wniosek, również w przypadku zwierząt z ww. załącznika nr 1.',
          '',
          'Pewna kobieta posiadała **małpę rezusa**, urodzoną w hodowli i przystosowaną do życia w jej domu. Zwierzę było oswojone i wychowane w warunkach domowych.',
          '',
          '**RDOŚ** odmówił jej udzielenia zezwolenia na odstępstwo od zakazu posiadania małpy rezusa, uzasadniając, że **małpa rezus należy do ww. I kategorii**, a zatem jej posiadanie i przetrzymywanie jest zarezerwowane dla:',
        ],
        list: [
          'ogrodów zoologicznych,',
          'cyrków,',
          'placówek naukowych, prowadzących badania nad zwierzętami,',
          'ośrodków rehabilitacji zwierząt.',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: [
          'Decyzja ta została utrzymana przez Generalnego Dyrektora Ochrony Środowiska i oddalona przez Wojewódzki Sąd Administracyjny w Warszawie.',
          '',
          'Argumentacja była podobna, jak w przypadku **RDOŚ**.',
        ],
      },
      {
        heading: 'Sukces w Naczelnym Sądzie Administracyjnym',
        paragraphs: [
          '**Naczelny Sąd Administracyjny uwzględnił skargę kasacyjną.**',
          '',
          'Podkreślił, że od momentu narodzin małpa została przystosowana do życia w ludzkiej rodzinie.',
          '',
          'Zwierzę jest w pełni oswojone, nie zna zwyczajów ani codziennych nawyków swoich pobratymców. Przekazanie tego zwierzęcia do ogrodu zoologicznego oznacza skazanie go na walkę o przetrwanie w stadzie, a być może na rychłą śmierć.',
          '',
          'To samo czeka je w placówce naukowej lub w cyrku.',
          '',
          '**NSA przyjął, że nakazanie osobie prywatnej przekazania udomowionej małpy rezus makaka do ogrodu zoologicznego, do placówki naukowej, bądź do cyrku, jest nie do pogodzenia z normą art. 1 ust. 1 ustawy o ochronie zwierząt** z dnia 21 sierpnia 1997 roku, Dz.U. Nr 111, poz. 724 ze zm., z której wynika, że człowiek winien jest zwierzęciu poszanowanie, ochronę i opiekę.',
          '',
          'Jak widzisz, nawet jeśli prawo wydaje się nieprzychylne – **warto walczyć**.',
          '',
          'Przykład małpy rezusa pokazuje, że determinacja i znajomość przepisów może przechylić szalę zwycięstwa na Twoją korzyść.',
      '',
      'Do następnego. 🙂',
        ],
      },
    ],
    relatedSlugs: ['czy-jestem-wlascicielem', 'umowa-adopcyjna'],
ctaTitle: 'Chcesz mieć niebezpieczne zwierzę?',
ctaText:
  'Pomożemy w uzyskaniu zezwolenia, o którym mowa w artykule, lub zaproponujemy inne możliwe rozwiazania prawne.',
    translations: {
      en: {
        title: 'I want to have a dangerous animal!',
        teaser:
          'Wondering how to legally keep a dangerous animal? See how to obtain a permit from the Regional Directorate for Environmental Protection (RDOŚ) and what to do when the law seems unfriendly.',
        sections: [
          {
            heading: 'Safe or dangerous animal?',
            paragraphs: [
              'You are wondering how to legally keep a dangerous animal.',
              '',
              'The key is to determine whether the species you have chosen actually falls into the “dangerous” category.',
              '',
              '**Check Annex 1 and Annex 2 to the Regulation of the Minister for the Environment of 3 August 2011** on animal species dangerous to human life and health (Journal of Laws 2011 No. 173, item 1037, as amended).',
              '',
              '**If your chosen animal is not listed in these annexes**, this does not automatically mean that you can keep it without any restrictions. It may, for example, be a **protected species** – which is a topic for another article.',
            ],
          },
          {
            heading: 'Your chosen animal really is dangerous – what next?',
            paragraphs: [
              'Under **Article 73(1) of the Nature Protection Act** of 16 April 2004 (Journal of Laws No. 92, item 880, as amended) it is prohibited to:',
              '',
              '**1)** keep and possess,',
              '**2)** bring into Poland from abroad,',
              '**3)** sell, exchange, rent, donate or lend to unauthorised entities',
              '',
              '– live animals belonging to species dangerous to human life and health (the ban applies to animals listed **in both annexes** to the regulation).',
            ],
          },
          {
            heading: 'Obtain a special permit',
            paragraphs: [
              'If your chosen animal is listed **in Annex 2** (that is, it belongs to the **second category of dangerous animals**), you can submit an **application for a permit to derogate from the ban** on keeping it as a dangerous animal.',
              '',
              'The application should be submitted to the **Regional Directorate for Environmental Protection (RDOŚ)** competent for the place where the animal is to be kept.',
              '',
              'If your chosen animal is listed in **Annex 1** (the **first category of dangerous animals**), in theory such a permit cannot be granted at all (Article 73(4) of the Nature Protection Act).',
              '',
              'But even then it is **worth trying** to submit the application.',
              '',
              'If **RDOŚ** refuses to grant the permit, you can appeal to the **General Directorate for Environmental Protection**.',
              '',
              'If necessary, you can then take the case to the **Provincial Administrative Court**, and finally to the **Supreme Administrative Court**.',
              '',
              'Listen to a story that shows why it is worth submitting such an application even for animals from Annex 1.',
              '',
              'A woman kept a **rhesus macaque** that had been born in captivity and adapted to life in her home. The animal was tame and had been raised in domestic conditions.',
              '',
              '**RDOŚ** refused to grant her a derogation permit, arguing that the **rhesus macaque belongs to the first category of dangerous animals**, and therefore may only be kept by:',
            ],
            list: [
              'zoos,',
              'circuses,',
              'scientific institutions conducting research on animals,',
              'animal rehabilitation centres.',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: [
              'This decision was upheld by the General Directorate for Environmental Protection and then by the Provincial Administrative Court in Warsaw.',
              '',
              'The reasoning was similar to that of **RDOŚ**.',
            ],
          },
          {
            heading: 'Success before the Supreme Administrative Court',
            paragraphs: [
              'The **Supreme Administrative Court allowed the cassation appeal.**',
              '',
              'It emphasised that from birth the monkey had been adapted to life in a human family.',
              '',
              'The animal was fully tame; it did not know the habits or daily routines of animals of its own species. Sending it to a zoo would condemn it to a struggle for survival in a group and perhaps to a quick death.',
              '',
              'A similar fate would await it in a research facility or a circus.',
              '',
              'The Court held that **ordering a private person to transfer a domesticated rhesus macaque to a zoo, research institution or circus is incompatible with Article 1(1) of the Animal Protection Act** of 21 August 1997, which provides that humans owe animals respect, protection and care.',
              '',
              'As you can see, even when the law seems unfriendly, it is **worth fighting**.',
              '',
              'The story of the rhesus monkey shows that determination and knowledge of the law can tip the scales in your favour.',
              '',
              'Till next time. 🙂',
            ],
          },
        ],
 ctaTitle: 'Do you want to keep a dangerous animal?',
    ctaText:
      'We can help you obtain the permit described in the article or propose other possible legal steps.',
      },
      es: {
        title: '¡Quiero tener un animal peligroso!',
        teaser:
          '¿Te planteas cómo tener legalmente un animal peligroso? Descubre cómo obtener un permiso de la Dirección Regional de Protección del Medio Ambiente (RDOŚ) y qué hacer cuando la ley parece poco favorable.',
        sections: [
          {
            heading: '¿Animal seguro o peligroso?',
            paragraphs: [
              'Te preguntas cómo tener de forma legal un animal peligroso.',
              '',
              'Lo fundamental es determinar si la especie que has elegido entra realmente en la categoría de “peligrosa”.',
              '',
              '**Consulta los anexos 1 y 2 del Reglamento del Ministro de Medio Ambiente de 3 de agosto de 2011** sobre especies de animales peligrosos para la vida y la salud de las personas (DO 2011 nº 173, pos. 1037, con modificaciones).',
              '',
              '**Si tu elegido no figura en estos anexos**, eso no significa automáticamente que puedas tenerlo sin restricciones. Puede estar, por ejemplo, sujeto a **protección de especies**, de lo que hablaré en otro artículo.',
            ],
          },
          {
            heading: 'Tu elegido es realmente un animal peligroso – y ahora, ¿qué?',
            paragraphs: [
              'Conforme al **art. 73.1 de la Ley de Protección de la Naturaleza** de 16 de abril de 2004 (DO nº 92, pos. 880, con modificaciones) está prohibido:',
              '',
              '**1)** poseer y mantener,',
              '**2)** introducir en Polonia desde el extranjero,',
              '**3)** vender, intercambiar, arrendar, donar o prestar a entidades no autorizadas',
              '',
              '– animales vivos de especies peligrosas para la vida y la salud de las personas (la prohibición se aplica a los animales incluidos **en ambos anexos** del reglamento).',
            ],
          },
          {
            heading: 'Obtén un permiso especial',
            paragraphs: [
              'Si tu elegido figura **en el anexo 2** (es decir, pertenece a la **segunda categoría de animales peligrosos**), puedes presentar una **solicitud de permiso de excepción a la prohibición** de tenerlo como animal peligroso.',
              '',
              'La solicitud debe dirigirse a la **Dirección Regional de Protección del Medio Ambiente (RDOŚ)** competente para el lugar donde vaya a mantenerse el animal.',
              '',
              'Si tu elegido figura en el **anexo 1** (la **primera categoría de animales peligrosos**), en teoría no es posible obtener dicho permiso (art. 73.4 de la Ley de Protección de la Naturaleza).',
              '',
              'Pero aun así **merece la pena intentar** presentar la solicitud.',
              '',
              'Si **RDOŚ** deniega el permiso, puedes recurrir ante la **Dirección General de Protección del Medio Ambiente**.',
              '',
              'A continuación puedes acudir al **Tribunal Administrativo Regional** y, en último término, al **Tribunal Supremo Administrativo**.',
              '',
              'Escucha una historia que demuestra que merece la pena presentar la solicitud incluso en el caso de animales incluidos en el anexo 1.',
              '',
              'Una mujer tenía una **mona rhesus** nacida en cautividad y adaptada a la vida en su casa. El animal estaba domesticado y había sido criado en un entorno familiar.',
              '',
              '**RDOŚ** le denegó el permiso de excepción a la prohibición de tener la mona rhesus, alegando que **la mona pertenece a la primera categoría de animales peligrosos** y que, por tanto, su posesión y mantenimiento están reservados para:',
            ],
            list: [
              'zoos,',
              'circos,',
              'centros de investigación que realizan estudios con animales,',
              'centros de rehabilitación de animales.',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: [
              'Esta decisión fue confirmada por la Dirección General de Protección del Medio Ambiente y posteriormente por el Tribunal Administrativo Regional de Varsovia.',
              '',
              'La argumentación fue similar a la de **RDOŚ**.',
            ],
          },
          {
            heading: 'Éxito ante el Tribunal Supremo Administrativo',
            paragraphs: [
              'El **Tribunal Supremo Administrativo estimó el recurso de casación.**',
              '',
              'Subrayó que, desde su nacimiento, la mona había sido adaptada a la vida en una familia humana.',
              '',
              'El animal estaba completamente domesticado; no conocía las costumbres ni la vida cotidiana de sus congéneres. Enviarlo a un zoo significaría condenarlo a luchar por la supervivencia en un grupo e incluso a una muerte rápida.',
              '',
              'Lo mismo ocurriría en un centro de investigación o en un circo.',
              '',
              'El Tribunal consideró que **obligar a una persona particular a entregar una mona rhesus domesticada a un zoo, a un centro de investigación o a un circo es incompatible con el art. 1.1 de la Ley de Protección de los Animales** de 21 de agosto de 1997, según el cual el ser humano debe al animal respeto, protección y cuidado.',
              '',
              'Como ves, incluso cuando la ley parece poco favorable, **merece la pena luchar**.',
              '',
              'El ejemplo de la mona rhesus muestra que la determinación y el conocimiento de la normativa pueden inclinar la balanza a tu favor.',
              '',
              'Hasta la próxima. 🙂',
            ],
          },
        ],
 ctaTitle: '¿Quieres tener un animal peligroso?',
    ctaText:
      'Te ayudaremos a obtener el permiso mencionado en el artículo o te propondremos otras posibles vías legales.',
      },
    },
  },
  {
    slug: 'rekojmia-za-wady-zwierzecia',
    title: 'Rękojmia za wady zwierzęcia',
    category: 'Paragraf Zwierzoochronny',
    teaser: 'Kupiłeś zwierzę, które miało być rasowe i zdrowe, ale jest inaczej? Dowiedz się, czego możesz żądać od sprzedawcy.',
    date: '30 czerwca 2025',
    readingTime: '3 min czytania',
    link: '/blog/rekojmia-za-wady-zwierzecia.html',
    featured: false,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/rekojmia-za-wady-zwierzecia.jpg',
      alt: 'Pies u weterynarza - ilustracja do artykułu o rękojmi za wady zwierzęcia',
    },
    tags: [
      'czy zwierzę to rzecz',
      'mój pies miał być rasowy',
      'mój pies miał być zdrowy a jest chory',
      'odstąpienie od umowy sprzedaży zwierzęcia',
      'oszukali mnie w hodowli',
      'rękojmia przy sprzedaży zwierzęcia',
      'uprawnienia z rękojmi',
      'zwrot pieniędzy za nierasowe zwierzę',
      'zwrot zwierzęcia przy rękojmi',
    ],
    sections: [
      {
        heading: 'Miał być inny, niż jest!',
        paragraphs: [
          'Właśnie przyniosłeś do domu nowego zwierzaka?',
          '',
          'Sprzedawca mówił, że jest rasowy, zdrowy i w ogóle idealny?',
          '',
          'A okazało się, że… jest zupełnie odwrotnie?',
          '',
          'Możesz być rozczarowany.',
          '',
          'Możesz również podjąć konkretne kroki prawne. 😊',
        ],
      },
      {
        heading: 'Czym jest rękojmia za wady zwierzęcia?',
        paragraphs: [
          'Na pewno słyszałeś o rękojmi, czyli odpowiedzialności sprzedawcy za wady rzeczy.',
          '',
          'Ale: **zwierzę nie jest rzeczą**.',
          '',
          '**„Zwierzę, jako istota żyjąca, zdolna do odczuwania cierpienia, nie jest rzeczą. Człowiek jest mu winien poszanowanie, ochronę i opiekę."** (vide: art. 1 ust. 1 ustawy o ochronie zwierząt z dnia 21 sierpnia 1997 roku Dz.U. Nr 111, poz. 724 ze zm.).',
          '',
          'Do zwierząt stosuje się tylko odpowiednio, a nie wprost, przepisy o rzeczach.',
          '',
          'Oznacza to, że **rękojmia za wady zwierzęcia jest ograniczona** (w stosunku do rękojmi za wady rzeczy).',
        ],
      },
      {
        heading: 'Co możesz zrobić, gdy zwierzę ma "wady"?',
        paragraphs: [
          '**Masz prawo złożyć sprzedawcy oświadczenie o obniżeniu ceny zwierzęcia** (vide: art. 560 ustawy kodeks cywilny z dnia 23 kwietnia 1964 roku, Dz.U. Nr 16, poz. 93 ze zm.).',
          '',
          'Możesz żądać obniżenia ceny o:',
        ],
        list: [
          'koszty leczenia zwierzęcia (jeżeli choroba istniała już w momencie sprzedaży),',
          'różnicę między ceną zwierzęcia z rodowodem, które miałeś otrzymać, a faktycznie otrzymanym bez rodowodu.',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: [
          'Jeżeli sprzedawca nie zgodzi się na obniżkę ceny, idź do sądu.',
          '',
          'W sądzie to na Tobie spoczywa **ciężar udowodnienia, że zwierzę miało wadę w chwili sprzedaży**, taką jak choroba, lub brak rodowodu (vide: art. 6 ww. ustawy kodeks cywilny).',
        ],
      },
      {
        heading: 'Czego nie możesz zrobić, gdy zwierzę ma "wady"?',
        paragraphs: [
          '**Nie możesz żądać** usunięcia "wad" zwierzęcia przez sprzedawcę albo wymiany zwierzęcia na zwierzę wolne od "wad" (vide: ww. art. 560 ust. 1 ustawy kodeks cywilny).',
          '',
          '**Odstąpienie od umowy jest możliwe wtedy, kiedy zwierzę zmarło w wyniku wad istniejących w chwili sprzedaży.**',
          '',
          'Dlaczego wtedy?',
          '',
          'Bo strony zwracają sobie świadczenia wzajemne (vide: art. 496 ww. ustawy kodeks cywilny).',
          '',
          'A nie możemy zwrócić sprzedawcy zwierzęcia, tylko dlatego, że jest chore lub bez rodowodu (vide: ww. art. 1 ust. 1 ustawy o ochronie zwierząt).',
          '',
          'W razie śmierci zwierzęcia i odstąpienia od umowy sprzedaży, oczywiście nie wydajemy sprzedawcy truchła.',
          '',
          'W takim wypadku sprzedawca zwraca Tobie w całości zapłaconą cenę sprzedaży zwierzęcia.',
          '',
          '**Wyjątek?** Kupujący prowadzi działalność gospodarczą (hodowlę) – wtedy, możliwy jest, zdaniem orzecznictwa (niepozbawionego kontrowersji), zwrot zwierzęcia sprzedawcy, celem wymiany na zwierzę "wolne od wad" lub w następstwie odstąpienia od umowy (vide: wyrok Sądu Okręgowego w Olsztynie z dnia 11 grudnia 2017 roku, sygn. akt: IX Ca 803/17).',
        ],
      },
      {
        heading: 'Przykład z orzecznictwa',
        paragraphs: [
          '**"Zasadne jest odstąpienie od umowy kupna papugi, która po kilku dniach zdechła na skutek choroby istniejącej przed zakupem** (art. 556 § 1 KC w zw. z art. 556¹ § 1 KC). Skoro papuga zdechła na skutek wady istniejącej w chwili sprzedaży, to logiczne jest, że takie zwierzę nie podlega zwrotowi właścicielowi" (vide: wyrok Sądu Rejonowego w Głubczycach z dnia 9 lipca 2019 roku, sygn. akt: I C 292/18).',
          '',
          'Teraz już wiesz, z czym wiąże się rękojmia za wady zwierzęcia.',
          '',
          'Do następnego.',
        ],
      },
    ],
    relatedSlugs: ['czy-jestem-wlascicielem', 'umowa-adopcyjna'],
    ctaTitle: 'Kupiłeś zwierzę z „wadami”?',
    ctaText:
      'Jeżeli po sprzedaży zwierzęcia okazało się, że np. jest chore lub nie ma właściwości o których istnieniu zapewniał sprzedawca (np. rodowodu), zgłoś się do nas - pomożemmy w dochodzeniu roszczeń z tytułłu rękojmi.',
    translations: {
      en: {
        title: 'Statutory warranty for defects in an animal',
        teaser:
          'You bought an animal that was supposed to be purebred and healthy, but reality turned out different? Learn what rights you have under statutory warranty and what you can demand from the seller.',
        sections: [
          {
            heading: 'It was supposed to be different',
            paragraphs: [
              'You have just brought a new animal home.',
              '',
              'The seller assured you it was purebred, healthy and “just perfect”.',
              '',
              'And it turned out… quite the opposite?',
              '',
              'You may feel disappointed.',
              '',
              'But you can also take concrete legal steps. 😊',
            ],
          },
          {
            heading: 'What is statutory warranty for defects in an animal?',
            paragraphs: [
              'You have certainly heard of statutory warranty – the seller’s liability for defects in a thing.',
              '',
              'But: **an animal is not a thing**.',
              '',
              '"_An animal, as a living being capable of suffering, is not a thing. A human being is obliged to respect, protect and care for it._" (Article 1(1) of the Polish Animal Protection Act of 21 August 1997).',
              '',
              'Provisions on things apply to animals only **accordingly**, not directly.',
              '',
              'This means that **statutory warranty for defects in an animal is more limited** than warranty for defects in a thing.',
            ],
          },
          {
            heading: 'What can you do if the animal has “defects”?',
            paragraphs: [
              'You have the right to **submit a declaration to the seller demanding a reduction of the price** (Article 560 of the Polish Civil Code).',
              '',
              'You can request a price reduction by the amount of:',
            ],
            list: [
              'veterinary treatment costs (if the illness already existed at the moment of sale),',
              'the difference between the price of the animal with the pedigree you were supposed to receive and the value of the animal you actually received – without a pedigree.',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: [
              'If the seller refuses to reduce the price, you can take the case to court.',
              '',
              'In court it is **you** who must prove that the animal had a defect at the time of sale – for example an illness or lack of pedigree (Article 6 of the Polish Civil Code).',
            ],
          },
          {
            heading: 'What you cannot do when the animal has “defects”',
            paragraphs: [
              'You **cannot demand** that the seller remove the “defects” of the animal or replace it with an animal free from “defects” (Article 560(1) of the Polish Civil Code).',
              '',
              '**Withdrawal from the contract is possible only when the animal has died as a result of defects existing at the time of sale.**',
              '',
              'Why only then?',
              '',
              'Because the parties must return the performances they have received (Article 496 of the Civil Code).',
              '',
              'We cannot simply “return” an animal to the seller just because it is ill or does not have a pedigree (see again Article 1(1) of the Animal Protection Act).',
              '',
              'In the event of the animal’s death and withdrawal from the contract of sale, we do **not** hand over the carcass to the seller.',
              '',
              'In such a case the seller returns the entire purchase price to you.',
              '',
              '**Exception?** If the buyer runs a business (a breeding operation), case law – although not without controversy – accepts that the animal may be returned to the seller in order to be replaced with one “free of defects” or in connection with withdrawal from the contract (see the judgment of the District Court in Olsztyn of 11 December 2017, ref. IX Ca 803/17).',
            ],
          },
          {
            heading: 'Example from case law',
            paragraphs: [
              '"_It is justified to withdraw from a contract for the purchase of a parrot which died a few days later as a result of a disease existing before the purchase_ (Articles 556 § 1 and 556¹ § 1 of the Civil Code). Since the parrot died as a result of a defect existing at the time of sale, it is logical that such an animal cannot be returned to the owner" (judgment of the District Court in Głubczyce of 9 July 2019, ref. I C 292/18).',
              '',
              'You now know what statutory warranty for defects in an animal involves.',
              '',
              'Till next time.',
            ],
          },
        ],
        ctaTitle: 'Have you bought an animal with “defects”?',
    ctaText:
      'If after the sale it turns out that the animal is ill or lacks the qualities promised by the seller (for example, a pedigree), contact us – we will help you pursue claims based on statutory warranty.',
      },
      es: {
        title: 'Garantía por defectos de un animal',
        teaser:
          '¿Has comprado un animal que se suponía que iba a ser de pura raza y sano, pero la realidad es otra? Descubre qué derechos tienes en virtud de la garantía legal y qué puedes exigir al vendedor.',
        sections: [
          {
            heading: 'No es como te lo prometieron',
            paragraphs: [
              'Acabas de traer a casa a un nuevo compañero.',
              '',
              'El vendedor aseguraba que era de pura raza, sano y “perfecto en todos los sentidos”.',
              '',
              '¿Y ha resultado ser… todo lo contrario?',
              '',
              'Es normal sentirse decepcionado.',
              '',
              'Pero también puedes dar **pasos jurídicos concretos**. 😊',
            ],
          },
          {
            heading: 'Qué es la garantía por defectos de un animal',
            paragraphs: [
              'Seguramente has oído hablar de la garantía legal, es decir, de la responsabilidad del vendedor por defectos de una cosa.',
              '',
              'Pero: **el animal no es una cosa**.',
              '',
              '"_El animal, como ser vivo capaz de sufrir, no es una cosa. El ser humano le debe respeto, protección y cuidado._" (art. 1.1 de la Ley polaca de Protección de los Animales de 21 de agosto de 1997).',
              '',
              'Las disposiciones relativas a las cosas se aplican a los animales solo **de forma correspondiente**, no directamente.',
              '',
              'Esto significa que **la garantía por defectos de un animal está restringida** en comparación con la garantía por defectos de una cosa.',
            ],
          },
          {
            heading: 'Qué puedes hacer si el animal tiene “defectos”',
            paragraphs: [
              'Tienes derecho a **presentar al vendedor una declaración de reducción del precio** (art. 560 del Código Civil polaco).',
              '',
              'Puedes exigir una reducción del precio en la medida de:',
            ],
            list: [
              'los costes de tratamiento veterinario (si la enfermedad ya existía en el momento de la venta),',
              'la diferencia entre el precio del animal con pedigrí que debías recibir y el valor del animal que realmente has recibido, sin pedigrí.',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: [
              'Si el vendedor no acepta reducir el precio, puedes acudir a los tribunales.',
              '',
              'En el juicio **recae sobre ti** la carga de demostrar que el animal tenía un defecto en el momento de la venta, por ejemplo una enfermedad o la falta de pedigrí (art. 6 del Código Civil).',
            ],
          },
          {
            heading: 'Qué no puedes hacer cuando el animal tiene “defectos”',
            paragraphs: [
              '**No puedes exigir** que el vendedor elimine los “defectos” del animal ni que lo sustituya por otro animal “sin defectos” (art. 560.1 del Código Civil).',
              '',
              '**La resolución del contrato solo es posible cuando el animal ha muerto a consecuencia de defectos existentes en el momento de la venta.**',
              '',
              '¿Por qué solo entonces?',
              '',
              'Porque las partes deben devolverse recíprocamente las prestaciones recibidas (art. 496 del Código Civil).',
              '',
              'No podemos simplemente “devolver” el animal al vendedor solo porque esté enfermo o no tenga pedigrí (de nuevo, véase el art. 1.1 de la Ley de Protección de los Animales).',
              '',
              'En caso de muerte del animal y resolución del contrato de compraventa, **no** entregamos el cadáver al vendedor.',
              '',
              'En ese caso, el vendedor te devuelve íntegramente el precio de compra.',
              '',
              '**Excepción:** cuando el comprador ejerce una actividad económica (cría), parte de la jurisprudencia –no exenta de controversia– admite la devolución del animal al vendedor para sustituirlo por otro “libre de defectos” o en relación con la resolución del contrato (sentencia del Tribunal Regional de Olsztyn de 11 de diciembre de 2017, ref. IX Ca 803/17).',
            ],
          },
          {
            heading: 'Ejemplo de jurisprudencia',
            paragraphs: [
              '"_Es justificada la resolución del contrato de compra de un loro que, pocos días después, muere a causa de una enfermedad existente antes de la compra_ (arts. 556 § 1 y 556¹ § 1 del Código Civil). Dado que el loro murió como consecuencia de un defecto existente en el momento de la venta, es lógico que dicho animal no pueda devolverse al propietario" (sentencia del Tribunal de Distrito de Głubczyce de 9 de julio de 2019, ref. I C 292/18).',
              '',
              'Ahora ya sabes en qué consiste la garantía por defectos de un animal.',
              '',
              'Hasta la próxima.',
            ],
          },
        ],
        ctaTitle: '¿Has comprado un animal con “defectos”?',
    ctaText:
      'Si después de la venta resulta que el animal está enfermo o no tiene las características prometidas por el vendedor (por ejemplo, pedigrí), ponte en contacto con nosotros: te ayudaremos a reclamar tus derechos en virtud de la garantía legal.',
      },
    },
  },
{
  slug: 'znecanie-sie-nad-zwierzetami',
  title: 'Znęcanie się nad zwierzętami- jak je definiuje prawo?',
  category: 'Paragraf Zwierzoochronny',
  teaser:
    'Czym jest znęcanie się nad zwierzętami w świetle polskiego prawa? Poznaj definicję oraz ustawowy katalog zachowań stanowiących znęcanie się nad zwierzęciem.',
  date: '11 lipca 2025',
  readingTime: '4 min czytania',
  link: '/blog/znecanie-sie-nad-zwierzetami.html',
  featured: false,
  isParagrafZwierzoochronny: true,
  featuredImage: {
    src: '/blog-images/znecanie-sie-nad-zwierzetami.png',
    alt: 'Smutny pies - ilustracja do artykułu o znęcaniu się nad zwierzętami',
  },
  tags: [
    'bicie psa',
    'bicie zwierząt',
    'definicja znęcania się',
    'katalog znęcania się nad zwierzętami',
    'okrutne traktowanie zwierząt',
    'przemoc wobec zwierząt',
    'znęcanie się nad zwierzętami',
  ],
  sections: [
    {
      heading: 'Definicja znęcania się nad zwierzętami',
      paragraphs: [
        'Zastanawiasz się, co w świetle prawa oznacza znęcanie się nad zwierzętami?',
        '',
        'Dobrze trafiłeś- już wyjaśniam.',
        '',
        '**„Zgodnie z ustawą o ochronie zwierząt z dnia 21 sierpnia 1997 roku, Dz.U. Nr 111, poz. 724 ze zm., znęcanie się, jest to zadawanie albo świadome dopuszczanie do zadawania zwierzęciu bólu lub cierpień (vide: art. 6 ust. 2 ww. ustawy o ochronie zwierząt). ".**',
        '',
        'Ważne: ból i cierpienie to nie to samo.',
        '',
        'Cierpienie jest pojęciem nadrzędnym i oprócz bólu obejmuje m.in: **głód, pragnienie, strach.**',
        '',
        'Ww. przepis ustawy o ochronie zwierząt zawiera katalog zachowań uznawanych za znęcanie się nad zwierzęciem.',
        '',
        'Co istotne- katalog ten ma charakter otwarty.',
        '',
        'Co to oznacza?',
        '',
        'Jeżeli dane zachowanie znajduje się w ustawowym katalogu – mamy do czynienia ze znęcaniem się.',
        '',
        'Znęcanie się to najczęściej działanie, ale bywa też zaniechaniem – czyli niedopełnieniem obowiązków wobec zwierzęcia.',
        '',
        ' Za znęcanie się może zostać uznane zaniechanie: **podawania zwierzęciu pokarmu lub napoju, sprzątania pomieszczenia, w którym przebywa zwierzę, zapewnienia zwierzęciu opieki weterynaryjnej.**',
        '',
      ],
    },
    {
      heading: 'Ustawowy katalog znęcania się nad zwierzętami',
      paragraphs: [
        'Ustawa o ochronie zwierząt, w art. 6 ust. 2, zawiera **katalog zachowań uznawanych za znęcanie się nad zwierzętami.**',
        '',
        'Poniżej omówię kilka przykładów znęcania się nad zwierzętami z ustawowego katalogu (vide: ww. art. 6 ust. 2 ustawy o ochronie zwierząt):',
        '',
      ],
      listStyle: 'paw',
      list: [
        '**Bicie** zwierząt, które wg ww. ustawy polega na:  1) uderzaniu go przedmiotami twardymi i ostrymi lub zaopatrzonymi w urządzenia obliczone na sprawianie bólu (w każdą część ciała zwierzęcia), 2) biciu zwierzęcia po głowie, dolnej części brzucha, dolnych częściach kończyn (każde bicie, a nie tylko ww. przedmiotami),',
        '**Złośliwe** straszenie lub **drażnienie** zwierząt.- Brzmi niewinnie? A jednak – złośliwe straszenie lub drażnienie (np. pukanie w szybę w zoo, rzucanie przedmiotami) jest przestępstwem,',
        'Utrzymywanie zwierząt w **niewłaściwych** warunkach **bytowania**- Właściwe warunki bytowania to zapewnienie zwierzęciu możliwości egzystencji, zgodnie z potrzebami danego gatunku, rasy, płci i wieku (vide: art. 4 ww. ustawy o ochronie zwierząt). Niezapewnienie właściwych warunków bytowania zwierzęciu to m.in.: utrzymywanie ich w stanie rażącego zaniedbania lub niechlujstwa, w pomieszczeniach albo klatkach uniemożliwiających im zachowanie naturalnej pozycji,',
        '**Porzucanie** zwierząt. - Wg ww. katalogu porzucanie dotyczy w szczególności psa lub kota. Porzucenia zwierzęcia może dopuścić się właściciel bądź osoba, pod której opieką zwierzę pozostaje. Zwierzę przywiązuje się do człowieka.  Kiedy człowiek je porzuca, cierpienie zwierzęcia jest niewyobrażalne. Dodam tylko, że porzucający zwierzę traci jego “własność” (vide: art. 181 kc), ',
        '**Narażenie zwierząt na warunki atmosferyczne, które zagrażają zdrowiu lub życiu** zwierzęcia- Przykłady:  1) pozostawienie zwierzęcia na balkonie w upale lub mrozie, 2) zamknięcie zwierzęcia w samochodzie przy wysokiej temperaturze,',
        'Niezapewnienie zwierzęciu **dostępu do pokarmu lub wody**- Wg ww. katalogu znęcaniem się jest niezapewnienie zwierzęciu dostępu do pokarmu lub wody przez okres wykraczający poza minimalne potrzeby właściwe dla gatunku.',
      ],
    },
  ],
  relatedSlugs: ['odszkodowanie-za-pogryzione-zwierze', 'zadoscuczynienie-za-pogryzione-zwierze'],
  ctaTitle: 'Nie wiesz, co możesz zrobić, kiedy ktoś znęca się nad zwierzęciem?',
  ctaText: 'Zgłoś się do nas, pomożemy w podjęciu odpowiednich kroków prawnych.',
  translations: {
    en: {
      title: 'Animal abuse — how is it defined by law?',
      teaser:
        'What constitutes animal abuse under Polish law? Learn the legal definition and the statutory catalogue of behaviours that may be recognised as abusing an animal.',
      sections: [
        {
          heading: 'Definition of animal abuse',
          paragraphs: [
            'Are you wondering what animal abuse means under Polish law?',
            '',
            'You have come to the right place – let me explain.',
            '',
            '**“Under the Polish Animal Protection Act of 21 August 1997 (Journal of Laws No. 111, item 724, as amended), animal abuse means inflicting pain or suffering on an animal, or knowingly allowing such pain or suffering to be inflicted (see Article 6(2) of the Act).”**',
            '',
            'Important: pain and suffering are not the same.',
            '',
            'Suffering is a broader concept and, apart from pain, includes for example **hunger, thirst and fear**.',
            '',
            'The above provision of the Animal Protection Act contains a catalogue of behaviours considered to constitute animal abuse.',
            '',
            'Importantly, this catalogue is open-ended.',
            '',
            'What does this mean?',
            '',
            'If a given behaviour falls within the statutory catalogue, it constitutes animal abuse.',
            '',
            'Animal abuse most often takes the form of an action, but it may also consist of an omission – that is, a failure to fulfil one’s duties towards an animal.',
            '',
            'An omission may be considered animal abuse, in particular the **failure to provide food or water, to clean the premises in which the animal is kept, or to ensure veterinary care**.',
            '',
          ],
        },
        {
          heading: 'Statutory catalogue of animal abuse',
          paragraphs: [
            'The Animal Protection Act, in Article 6(2), contains a **catalogue of behaviours recognised as animal abuse**.',
            '',
            'Below, I discuss several examples of animal abuse listed in the statutory catalogue (see the above-mentioned Article 6(2) of the Animal Protection Act):',
            '',
          ],
          listStyle: 'paw',
          list: [
            '**Beating** animals – which, under the Act, includes: (1) striking an animal with hard or sharp objects or with devices designed to cause pain (to any part of the animal’s body), and (2) beating an animal on the head, lower abdomen or lower parts of the limbs (any beating, not only with the objects mentioned above),',
            '**Malicious scaring or teasing** of animals – it may sound harmless, but malicious scaring or teasing (e.g. knocking on glass at a zoo, throwing objects at animals) constitutes a criminal offence,',
            'Keeping animals in **improper living conditions** – proper living conditions mean ensuring that an animal can exist in accordance with the needs of its species, breed, sex and age (see Article 4 of the Animal Protection Act). Failure to provide proper living conditions includes, among others: keeping animals in a state of gross neglect or filth, or in rooms or cages that prevent them from maintaining a natural posture,',
            '**Abandonment** of animals – according to the statutory catalogue, abandonment applies in particular to dogs and cats. An animal may be abandoned by its owner or by a person under whose care the animal remains. Animals form bonds with humans. When a human abandons them, the animal’s suffering is unimaginable. It should also be noted that the person abandoning the animal loses their “ownership” rights (see Article 181 of the Polish Civil Code),',
            '**Exposing animals to weather conditions that endanger their health or life** – examples include: (1) leaving an animal on a balcony during extreme heat or frost, (2) locking an animal in a car during high temperatures,',
            'Failure to ensure **access to food or water** – under the statutory catalogue, animal abuse includes depriving an animal of access to food or water for a period exceeding the minimum needs appropriate for the given species.',
          ],
        },
      ],
      ctaTitle: 'Not sure what you can do when someone is abusing an animal?',
      ctaText: 'Contact us – we will help you take the appropriate legal steps.',
    },
    es: {
      title: 'Maltrato animal: ¿cómo lo define la ley?',
      teaser:
        '¿Qué se considera maltrato animal según el derecho polaco? Conoce la definición legal y el catálogo de conductas que pueden calificarse como maltrato hacia un animal.',
      sections: [
        {
          heading: 'Definición del maltrato animal',
          paragraphs: [
            '¿Te preguntas qué significa el maltrato animal desde el punto de vista legal?',
            '',
            'Has llegado al lugar adecuado: ya te lo explico.',
            '',
            '**“De conformidad con la Ley polaca de Protección de los Animales de 21 de agosto de 1997 (Diario Oficial n.º 111, posición 724, con modificaciones), el maltrato animal consiste en causar dolor o sufrimiento a un animal, o permitir conscientemente que se le cause dicho dolor o sufrimiento (véase el artículo 6, apartado 2, de la citada ley).”**',
            '',
            'Importante: el dolor y el sufrimiento no son lo mismo.',
            '',
            'El sufrimiento es un concepto más amplio y, además del dolor, incluye, entre otros, **el hambre, la sed y el miedo**.',
            '',
            'La disposición mencionada de la Ley de Protección de los Animales contiene un catálogo de conductas consideradas como maltrato animal.',
            '',
            'Lo importante es que dicho catálogo tiene carácter abierto.',
            '',
            '¿Qué significa esto?',
            '',
            'Si una conducta determinada se encuentra dentro del catálogo legal, estamos ante un caso de maltrato animal.',
            '',
            'El maltrato animal suele consistir en una acción, pero también puede darse por omisión, es decir, por el incumplimiento de las obligaciones hacia el animal.',
            '',
            'Puede considerarse maltrato animal, en particular, la **falta de suministro de alimento o agua, la falta de limpieza del lugar en el que se encuentra el animal, o la falta de atención veterinaria**.',
            '',
          ],
        },
        {
          heading: 'Catálogo legal de conductas constitutivas de maltrato animal',
          paragraphs: [
            'La Ley de Protección de los Animales, en su artículo 6, apartado 2, contiene un **catálogo de conductas consideradas como maltrato animal**.',
            '',
            'A continuación, analizo algunos ejemplos de maltrato animal incluidos en dicho catálogo legal (véase el artículo 6, apartado 2, de la Ley de Protección de los Animales):',
            '',
          ],
          listStyle: 'paw',
          list: [
            '**Golpear** a los animales – lo que, conforme a la ley, incluye: (1) golpear al animal con objetos duros o afilados o con dispositivos diseñados para causar dolor (en cualquier parte del cuerpo), y (2) golpear al animal en la cabeza, la parte inferior del abdomen o las extremidades inferiores (cualquier golpe, no solo con los objetos mencionados),',
            '**Asustar o provocar de forma malintencionada** a los animales – puede parecer inofensivo, pero el asustar o provocar de forma maliciosa (por ejemplo, golpear los cristales en un zoológico o lanzar objetos a los animales) constituye un delito,',
            'Mantener a los animales en **condiciones de vida inadecuadas** – las condiciones adecuadas implican garantizar al animal la posibilidad de vivir de acuerdo con las necesidades de su especie, raza, sexo y edad (véase el artículo 4 de la Ley de Protección de los Animales). No proporcionar condiciones adecuadas incluye, entre otros: mantener a los animales en un estado de abandono grave o suciedad, o en locales o jaulas que les impidan adoptar una postura natural,',
            '**Abandono** de animales – según el catálogo legal, el abandono se refiere en particular a perros y gatos. El abandono puede ser cometido por el propietario o por la persona bajo cuya custodia se encuentra el animal. Los animales establecen vínculos con los seres humanos. Cuando un ser humano los abandona, el sufrimiento del animal es inimaginable. Cabe añadir que la persona que abandona al animal pierde sus derechos de “propiedad” (véase el artículo 181 del Código Civil polaco),',
            '**Exponer a los animales a condiciones meteorológicas que pongan en peligro su salud o su vida** – por ejemplo: (1) dejar al animal en un balcón durante calor extremo o heladas, (2) encerrar al animal en un coche a altas temperaturas,',
            'No garantizar el **acceso a alimento o agua** – según el catálogo legal, constituye maltrato animal privar al animal de acceso a comida o agua durante un período que exceda las necesidades mínimas propias de su especie.',
          ],
        },
      ],
      ctaTitle: '¿No sabes qué hacer cuando alguien maltrata a un animal?',
      ctaText: 'Ponte en contacto con nosotros: te ayudaremos a tomar las medidas legales adecuadas.',
    },
  },
},
{
  slug: 'wlasnosc-znalezionego-zwierzecia',
  title: 'Własność znalezionego zwierzęcia',
  category: 'Paragraf Zwierzoochronny',
  teaser:
    'Znalazłeś psa albo kota bez opieki? Sprawdź, kiedy musisz szukać „właściciela”, kiedy zawiadomić starostę, a kiedy zwierzę staje się „twoje” — oraz czy możesz otrzymać znaleźne.',
  date: '—', // <- uzupełnij jak w pozostałych wpisach (np. '8 stycznia 2026')
  readingTime: '5 min czytania',
  link: '/blog/wlasnosc-znalezionego-zwierzecia.html',
  featured: false,
  isParagrafZwierzoochronny: true,
  featuredImage: {
    src: '/blog-images/wlasnosc-znalezionego-zwierzecia.jpg', // <- podmień na nazwę Twojego pliku
    alt: 'Znalezione zwierzę – co zrobić i kiedy staje się „twoje”',
  },
  tags: [
    'czy muszę szukać właściciela',
    'gdzie znajdę właściciela kota',
    'gdzie znajdę właściciela psa',
    'jak szukać właściciela psa',
    'jak szukać właściciela zwierzęcia',
    'jak znajdę właściciela kota',
    'jak znaleźć właściciela kota',
    'jak znaleźć właściciela psa',
    'kiedy stanę się właścicielem znalezionego psa',
    'kiedy znalezione zwierzę będzie moje',
    'pies przywiązany do drzewa',
    'po jakim czasie będę właścicielem znalezionego psa',
    'porzucone zwierzę',
    'utrata własności zwierzęcia',
  ],
  sections: [
    {
      heading: 'NIESPODZIEWANY BIEG WYDARZEŃ',
      paragraphs: [
        'Wyszedłeś na spacer i spotkałeś zwierzę bez opieki?',
        '',
        'Zadaj sobie kluczowe pytanie: zwierzę zgubiło się, uciekło, czy zostało porzucone?',
        '',
        'To ważne, bo od odpowiedzi zależy, co możesz – a nawet musisz – zrobić.',
        '',
        'Zobacz, to nie takie trudne.',
        '',
        'Zadbany kot z obrożą, który błąka się po osiedlu, najprawdopodobniej zgubił się albo uciekł.',
        '',
        'Pies, przywiązany do drzewa w lesie, najprawdopodobniej został porzucony.',
      ],
    },
    {
      heading: 'ZWIERZĘ ZGUBIŁO SIĘ ALBO UCIEKŁO',
      paragraphs: [
        'Załóżmy, że zwierzę zgubiło się albo uciekło.',
        '',
        'Z pomocą przychodzi nam **ustawa o rzeczach znalezionych z dnia 20 lutego 2015 roku, Dz.U. z 2015 roku poz. 397 ze zm.**',
        '',
        'Zgodnie z art. 2 ww. ustawy, przepisy o rzeczach znalezionych stosuje się odpowiednio do zwierząt, które zabłąkały się lub uciekły.',
        '',
        'Co to znaczy „odpowiednio”?',
        '',
        'To znaczy: z uwzględnieniem faktu, że zwierzę to nie rzecz, tylko istota żywa.',
        '',
        'Ok, do rzeczy… tzn. do zwierzęcia! Co z nim zrobić?',
        '',
        'Zawiadom “właściciela” (mówiąc po prawniczemu: **osobę uprawnioną do odbioru zwierzęcia**) o znalezieniu zwierzęcia i wezwij go do odbioru (vide: art. 4 ust. 1 ww. ustawy o rzeczach znalezionych).',
        '',
        'Jeżeli nie znasz “właściciela”, to zawiadom **starostę**.',
        '',
        'Znajdź starostę właściwego ze względu na:',
      ],
      listStyle: 'paw',
      list: [
        'Twoje miejsce zamieszkania,',
        'lub miejsce znalezienia zwierzęcia (vide: art. 5 ust. 1 ww. ustawy o rzeczach znalezionych).',
      ],
    },
    {
      heading: '',
      paragraphs: [
        'W jakim terminie masz zawiadomić “właściciela” albo starostę?',
        '',
        '**Niezwłocznie** 😊.',
        '',
        'A co, jeżeli “właściciel” się nie zgłosi?',
        '',
        'Zwierzę jest “Twoje” po upływie:',
        '',
        '— **roku** od dnia odbioru przez “właściciela” wezwania do odbioru,',
        'albo',
        '— **dwóch lat** od dnia znalezienia zwierzęcia, jeżeli zawiadomienie “właściciela” nie było możliwe',
        '',
        '(vide: art. 187 Kodeksu cywilnego z dnia 23 kwietnia 1964 roku, Dz.U. Nr 16, poz. 93 ze zm.).',
      ],
    },
    {
      heading: 'OTRZYMAJ ZNALEŹNE!',
      paragraphs: [
        'Śmiejesz się z tego, jakie to prawo nieżyciowe?!',
        '',
        'Przecież wystarczy dać ogłoszenie na fejsie lub OLX!',
        '',
        'Owszem, może wystarczy!',
        '',
        'Ale jeżeli zrobisz wszystko zgodnie z ww. ustawą o rzeczach znalezionych, to możesz liczyć na **znaleźne w wysokości 1/10 wartości zwierzęcia** (vide: art. 10 ust. 1 ww. ustawy o rzeczach znalezionych).',
      ],
    },
    {
      heading: 'PORZUCENIE ZWIERZĘCIA',
      paragraphs: [
        'Przejdźmy teraz do przypadku porzucenia zwierzęcia.',
        '',
        'Tu sytuacja wygląda zupełnie inaczej.',
        '',
        'Zwierzę porzucone staje się niczyje. “Własność” porzuconego zwierzęcia nabywa ten, kto je przygarnął.',
        '',
        'Mówiąc po prawniczemu, “własność” porzuconego zwierzęcia nabywa ten, kto objął je w **posiadanie samoistne** (vide: art. 181 Kodeksu cywilnego).',
        '',
        'Jeżeli oddasz zwierzę do schroniska, to jego “własność” nabędzie schronisko.',
        '',
        'Nie trzeba wzywać do odbioru osoby, która porzuciła zwierzę, ani zawiadamiać starosty.',
        '',
        'Jeszcze jedno.',
        '',
        'Pamiętaj, że ww. ustawy o rzeczach znalezionych **nie stosuje się** do zwierząt przekazanych do Centralnego Azylu dla Zwierząt, o którym mowa w ustawie z dnia 4 listopada 2022 roku o Centralnym Azylu dla Zwierząt, Dz.U. z 2022 r. poz. 2375 ze zm.',
        '',
        'Do następnego. 🙂',
      ],
    },
  ],
  relatedSlugs: ['czy-jestem-wlascicielem', 'umowa-adopcyjna'],
  ctaTitle: 'Znalazłeś zwierzę i nie wiesz, co zrobić?',
  ctaText:
    'Skontaktuj się z nami — podpowiemy, jakie kroki prawne podjąć.',
  translations: {
    en: {
      title: 'Ownership of a found animal',
      teaser:
        'Found a dog or cat with no guardian? Check when you must look for the “owner”, when to notify the county head (starosta), when the animal can become “yours”, and whether you may be entitled to a finder’s fee.',
      sections: [
        {
          heading: 'AN UNEXPECTED TURN OF EVENTS',
          paragraphs: [
            'You went for a walk and came across an animal without anyone looking after it?',
            '',
            'Ask yourself a key question: did the animal get lost, run away, or was it abandoned?',
            '',
            'This matters, because the answer determines what you may — and sometimes must — do.',
            '',
            'It’s not that hard.',
            '',
            'A well-kept cat with a collar wandering around the neighbourhood most likely got lost or escaped.',
            '',
            'A dog tied to a tree in the woods was most likely abandoned.',
          ],
        },
        {
          heading: 'THE ANIMAL GOT LOST OR RAN AWAY',
          paragraphs: [
            'Let’s assume the animal got lost or ran away.',
            '',
            'In Poland, the **Act on Found Property of 20 February 2015 (Journal of Laws 2015, item 397, as amended)** comes into play.',
            '',
            'Under Article 2 of that Act, the rules on found property apply accordingly to animals that have strayed or escaped.',
            '',
            'What does “accordingly” mean?',
            '',
            'It means: with the important caveat that an animal is not a thing, but a living being.',
            '',
            'So what should you do?',
            '',
            'Notify the “owner” (in legal terms: the **person entitled to collect the animal**) that you have found the animal and call on them to collect it (see Article 4(1) of the Act).',
            '',
            'If you do not know who the “owner” is, notify the **county head (starosta)**.',
            '',
            'Find the competent starosta based on:',
          ],
          listStyle: 'paw',
          list: [
            'your place of residence,',
            'or the place where you found the animal (see Article 5(1) of the Act).',
          ],
        },
        {
          heading: '',
          paragraphs: [
            'When should you notify the “owner” or the starosta?',
            '',
            '**Immediately** 😊.',
            '',
            'What if the “owner” does not come forward?',
            '',
            'The animal may become “yours” after:',
            '',
            '— **one year** from the day the “owner” receives your request to collect the animal,',
            'or',
            '— **two years** from the day you found the animal, if notifying the “owner” was not possible',
            '',
            '(see Article 187 of the Polish Civil Code of 23 April 1964).',
          ],
        },
        {
          heading: 'GET A FINDER’S FEE!',
          paragraphs: [
            'Does this sound unrealistically formal?',
            '',
            'After all, isn’t a Facebook or OLX post enough?',
            '',
            'Sometimes it is.',
            '',
            'But if you follow the procedure under the Act on Found Property, you may be entitled to a **finder’s fee of 1/10 of the animal’s value** (see Article 10(1) of the Act).',
          ],
        },
        {
          heading: 'ABANDONMENT OF AN ANIMAL',
          paragraphs: [
            'Now let’s move to the case of abandonment.',
            '',
            'Here the situation is different.',
            '',
            'An abandoned animal becomes ownerless. The “ownership” of an abandoned animal is acquired by the person who takes it in.',
            '',
            'In legal terms, it is acquired by the person who takes the animal into **independent possession** (see Article 181 of the Polish Civil Code).',
            '',
            'If you hand the animal over to a shelter, the shelter may acquire that “ownership”.',
            '',
            'There is no need to call on the person who abandoned the animal to collect it, or to notify the starosta.',
            '',
            'One more thing.',
            '',
            'Remember that the Act on Found Property **does not apply** to animals transferred to the Central Animal Shelter referred to in the Act of 4 November 2022 on the Central Animal Shelter (Journal of Laws 2022, item 2375, as amended).',
            '',
            'See you next time. 🙂',
          ],
        },
      ],
      ctaTitle: 'Found an animal and not sure what to do?',
      ctaText:
        'Contact us — we’ll advise you on the right legal steps.',
    },
    es: {
      title: 'La “propiedad” de un animal encontrado',
      teaser:
        '¿Has encontrado un perro o un gato sin cuidador? Descubre cuándo debes buscar al “propietario”, cuándo avisar al starosta, cuándo el animal puede llegar a ser “tuyo” y si puedes tener derecho a una recompensa por hallazgo.',
      sections: [
        {
          heading: 'UN GIRO INESPERADO DE LOS ACONTECIMIENTOS',
          paragraphs: [
            '¿Saliste a pasear y te encontraste con un animal sin supervisión?',
            '',
            'Hazte una pregunta clave: ¿se ha perdido, se ha escapado o ha sido abandonado?',
            '',
            'Es importante, porque de la respuesta depende qué puedes — e incluso debes — hacer.',
            '',
            'No es tan difícil.',
            '',
            'Un gato bien cuidado con collar que deambula por el barrio probablemente se ha perdido o se ha escapado.',
            '',
            'Un perro atado a un árbol en el bosque probablemente ha sido abandonado.',
          ],
        },
        {
          heading: 'EL ANIMAL SE PERDIÓ O SE ESCAPÓ',
          paragraphs: [
            'Supongamos que el animal se perdió o se escapó.',
            '',
            'En Polonia se aplica la **Ley de Objetos Encontrados de 20 de febrero de 2015 (Dz.U. 2015, pos. 397, con modificaciones)**.',
            '',
            'Según el art. 2 de dicha ley, las normas sobre objetos encontrados se aplican de forma análoga a los animales que se hayan extraviado o escapado.',
            '',
            '¿Qué significa “de forma análoga”?',
            '',
            'Significa: teniendo en cuenta que un animal no es una cosa, sino un ser vivo.',
            '',
            'Entonces, ¿qué deberías hacer?',
            '',
            'Informa al “propietario” (en términos jurídicos: la **persona legitimada para recoger el animal**) de que has encontrado al animal y requiérele para que lo recoja (véase el art. 4.1 de la ley).',
            '',
            'Si no conoces al “propietario”, informa al **starosta**.',
            '',
            'Busca el starosta competente por:',
          ],
          listStyle: 'paw',
          list: [
            'tu lugar de residencia,',
            'o el lugar donde encontraste al animal (véase el art. 5.1 de la ley).',
          ],
        },
        {
          heading: '',
          paragraphs: [
            '¿En qué plazo debes informar al “propietario” o al starosta?',
            '',
            '**Sin demora** 😊.',
            '',
            '¿Y si el “propietario” no aparece?',
            '',
            'El animal puede llegar a ser “tuyo” tras:',
            '',
            '— **un año** desde que el “propietario” reciba el requerimiento de recogida,',
            'o',
            '— **dos años** desde el hallazgo, si no fue posible notificar al “propietario”',
            '',
            '(véase el art. 187 del Código Civil polaco de 23 de abril de 1964).',
          ],
        },
        {
          heading: '¡RECIBE UNA RECOMPENSA POR HALLAZGO!',
          paragraphs: [
            '¿Te parece que esta normativa es poco “realista”?',
            '',
            'Al fin y al cabo, ¿no basta con publicar un anuncio en Facebook o en OLX?',
            '',
            'A veces sí.',
            '',
            'Pero si sigues el procedimiento previsto en la ley, puedes tener derecho a una **recompensa equivalente a 1/10 del valor del animal** (véase el art. 10.1 de la ley).',
          ],
        },
        {
          heading: 'ABANDONO DE UN ANIMAL',
          paragraphs: [
            'Pasemos ahora al caso de abandono.',
            '',
            'Aquí la situación es distinta.',
            '',
            'Un animal abandonado pasa a no tener dueño. La “propiedad” del animal abandonado la adquiere quien lo acoge.',
            '',
            'En términos jurídicos: quien lo toma en **posesión independiente** (véase el art. 181 del Código Civil polaco).',
            '',
            'Si entregas el animal a un refugio, el refugio puede adquirir esa “propiedad”.',
            '',
            'No hace falta requerir a la persona que lo abandonó ni avisar al starosta.',
            '',
            'Una cosa más.',
            '',
            'Recuerda que la Ley de Objetos Encontrados **no se aplica** a animales trasladados al Refugio Central para Animales previsto en la ley de 4 de noviembre de 2022 (Dz.U. 2022, pos. 2375, con modificaciones).',
            '',
            'Hasta la próxima. 🙂',
          ],
        },
      ],
      ctaTitle: '¿Has encontrado un animal y no sabes qué hacer?',
      ctaText:
        'Contacta con nosotros — te indicaremos los pasos jurídicos adecuados.',
    },
  },
},
 {
  slug: 'rewolucja-w-legalizacji-pobytu-mos-2026',
  title: 'Rewolucja w legalizacji pobytu – co nam przyniesie 2026 rok?',
  category: 'Legalizacja pobytu',
  teaser:
    'W 2026 roku planowane jest pełne wdrożenie systemu MOS, który ma ujednolicić i zinformatyzować składanie wniosków pobytowych w całej Polsce.',
  date: '9 stycznia 2026',
  readingTime: '5 min czytania',
  link: '/blog/rewolucja-w-legalizacji-pobytu-mos-2026.html',
  sections: [
    {
      heading: 'Wprowadzenie',
      paragraphs: [
        'W 2026 roku planowane jest pełne wdrożenie systemu informatycznego MOS (który już działa, ale w ograniczonym zakresie).',
        'Ww. system ma zapewnić całkowitą informatyzację składania wniosków pobytowych i jednocześnie ujednolicenie procedury składania ww. wniosków na obszarze całego kraju.',
      ],
    },
    {
      heading: 'Jak to wygląda dziś?',
      paragraphs: [
        'Obecnie wnioskodawca, zazwyczaj (procedura różni się w zależności od województwa):',
        '• składa na piśmie (listem poleconym), wniosek o zezwolenie na pobyt, a następnie dostaje od wojewody wezwanie do stawienia się w urzędzie wojewódzkim (celem pobrania odcisków palców),',
        'albo',
        '• generuje ww. wniosek w systemie informatycznym danego urzędu wojewódzkiego, a następnie składa ww. wniosek z własnoręcznym podpisem w trakcie wizyty w urzędzie wojewódzkim (w trakcie tej wizyty pobierane są odciski palców wnioskodawcy).',
      ],
    },
    {
      heading: 'Co oznacza pełne wdrożenie MOS?',
      paragraphs: [
        'System MOS ma być jedynym właściwym, ogólnopolskim systemem teleinformatycznym, za pośrednictwem którego cudzoziemcy będą składać wnioski pobytowe.',
        'Wnioski pobytowe składane poza systemem MOS będą pozostawiane bez rozpoznania.',
        'Składanie ww. wniosków odbywać się będzie w całości elektronicznie (wymagane jest posiadanie przez wnioskodawcę kwalifikowanego podpisu elektronicznego lub podpisu ePuap), bez papierowych dokumentów.',
        'Zamiast stempla w paszporcie (jak dotychczas), wnioskodawca otrzyma (poprzez MOS) zaświadczenie o złożeniu wniosku o zezwolenie na pobyt.',
        'Ww. zaświadczenie potwierdzi legalność pobytu cudzoziemca (do czasu prawomocnego zakończenia postępowania o ww. zezwolenie).',
        'Minister właściwy do spraw wewnętrznych ma wydać komunikat o terminie pełnego wdrożenia systemu MOS.',
        'Dopóki tego nie zrobi, wnioski pobytowe będą składane w dotychczasowy sposób.',
      ],
    },
    {
      heading: 'Czy to koniec wizyt w urzędzie wojewódzkim?',
      paragraphs: [
        'Nie.',
        'Po pełnym wdrożeniu MOS, wizyta w urzędzie nadal będzie konieczna, w celu:',
        '• pobrania danych biometrycznych,',
        '• weryfikacji tożsamości,',
        '• złożenia wzoru podpisu.',
      ],
    },
    {
      heading: 'Czy wdrożenie MOS to zmiana na lepsze?',
      paragraphs: [
        'W teorii tak, gdyż to krok w stronę nowoczesnej, cyfrowej administracji i uproszczenia procedur pobytowych.',
        'W praktyce może być mniej kolorowo.',
        'Nie każdy cudzoziemiec ma dostęp do urządzeń informatycznych, a także kwalifikowanego podpisu elektronicznego lub podpisu ePuap (co jest niezbędne do złożenia wniosku w MOS-ie).',
        'Ponadto cudzoziemiec może chcieć skorzystać z pomocy pełnomocnika.',
        'Skorzystanie z takiej pomocy jest zasadne, gdyż przepisy dotyczące legalizacji pobytu są skomplikowane.',
        'Tymczasem MOS wymaga złożenia podpisu przez samego cudzoziemca, a nie pełnomocnika.',
        'Nie wyklucza to udziału pełnomocnika, ale go utrudnia.',
        'Reasumując, to czy MOS to zmiana na lepsze, okaże się dopiero po pełnym wdrożeniu tego systemu.',
      ],
    },
  ],
  relatedSlugs: ['prawo-imigracyjne-2025-kluczowe-zmiany', 'jak-przygotowac-sie-do-rozmowy-z-prawnikiem'],
  ctaTitle: 'Masz sprawę z zakresu legalizacji pobytu?',
  ctaText:
    'Jeżeli planujesz złożenie wniosku pobytowego lub masz postępowanie w toku, możemy przeanalizować Twoją sytuację i pomóc bezpiecznie przejść przez procedurę.',
  translations: {
    en: {
      title: 'A revolution in residence legalisation – what will 2026 bring?',
      teaser:
        'In 2026, Poland plans to fully implement the MOS system, intended to standardise and digitise the submission of residence applications nationwide.',
      sections: [
        {
          heading: 'Introduction',
          paragraphs: [
            'In 2026, a full rollout of the MOS IT system is planned (it already operates, but only to a limited extent).',
            'This system is intended to ensure full digitisation of submitting residence applications and, at the same time, to standardise the procedure for filing such applications across the entire country.',
          ],
        },
        {
          heading: 'How does it look today?',
          paragraphs: [
            'Currently, the applicant typically (the procedure varies depending on the voivodeship):',
            '• submits in writing (by registered mail) an application for a residence permit and then receives from the voivode a summons to appear at the voivodeship office (to collect fingerprints),',
            'or',
            '• generates the application in the IT system of the relevant voivodeship office and then submits it with a handwritten signature during an in-person visit to the voivodeship office (during that visit, the applicant’s fingerprints are collected).',
          ],
        },
        {
          heading: 'What does full MOS implementation mean?',
          paragraphs: [
            'The MOS system is to be the only competent nationwide tele-IT system through which foreigners will submit residence applications.',
            'Residence applications submitted outside MOS will be left without examination.',
            'Submission of such applications will be carried out fully electronically (the applicant must have a qualified electronic signature or an ePUAP signature), without paper documents.',
            'Instead of a passport stamp (as before), the applicant will receive (via MOS) a certificate confirming submission of the residence-permit application.',
            'This certificate will confirm the legality of the foreigner’s stay (until the proceedings for the permit are finally concluded).',
            'The minister competent for internal affairs is to issue an official communication on the date of full implementation of the MOS system.',
            'Until that happens, residence applications will be submitted in the current manner.',
          ],
        },
        {
          heading: 'Is this the end of in-person visits to the voivodeship office?',
          paragraphs: [
            'No.',
            'After full MOS implementation, a visit to the office will still be necessary in order to:',
            '• collect biometric data,',
            '• verify identity,',
            '• submit a specimen signature.',
          ],
        },
        {
          heading: 'Is MOS implementation a change for the better?',
          paragraphs: [
            'In theory, yes, as it is a step toward modern, digital administration and simplifying residence procedures.',
            'In practice, it may be less straightforward.',
            'Not every foreigner has access to IT devices, as well as a qualified electronic signature or an ePUAP signature (which is necessary to submit an application in MOS).',
            'In addition, a foreigner may want to use the assistance of an attorney/representative.',
            'Using such assistance is justified, as residence legalisation regulations are complex.',
            'Meanwhile, MOS requires the signature to be provided by the foreigner themselves, not by the representative.',
            'This does not exclude the representative’s participation, but it makes it more difficult.',
            'In summary, whether MOS is a change for the better will only become clear after the system is fully implemented.',
          ],
        },
      ],
      ctaTitle: 'Do you have a residence legalisation matter?',
      ctaText:
        'If you plan to submit a residence application or already have proceedings underway, we can review your situation and help you navigate the procedure safely.',
    },
    es: {
      title: 'Una revolución en la legalización de la residencia: ¿qué traerá 2026?',
      teaser:
        'En 2026 se prevé la implantación completa del sistema MOS, destinado a unificar y digitalizar la presentación de solicitudes de residencia en toda Polonia.',
      sections: [
        {
          heading: 'Introducción',
          paragraphs: [
            'En 2026 está prevista la implantación completa del sistema informático MOS (que ya funciona, pero en un alcance limitado).',
            'Este sistema debe garantizar la informatización total de la presentación de solicitudes de residencia y, al mismo tiempo, la unificación del procedimiento de presentación de dichas solicitudes en todo el país.',
          ],
        },
        {
          heading: '¿Cómo se hace hoy?',
          paragraphs: [
            'Actualmente el solicitante, por lo general (el procedimiento varía según la voivodía):',
            '• presenta por escrito (por correo certificado) una solicitud de permiso de residencia y luego recibe del voivoda una citación para presentarse en la oficina (para la toma de huellas dactilares),',
            'o',
            '• genera la solicitud en el sistema informático de la oficina de la voivodía correspondiente y después la presenta con firma manuscrita durante una visita presencial (durante esa visita se recogen las huellas del solicitante).',
          ],
        },
        {
          heading: '¿Qué significa la implantación completa de MOS?',
          paragraphs: [
            'El sistema MOS debe ser el único sistema teleinformático nacional competente a través del cual las personas extranjeras presentarán solicitudes de residencia.',
            'Las solicitudes presentadas fuera del sistema MOS se dejarán sin tramitación.',
            'La presentación de dichas solicitudes se realizará íntegramente de forma electrónica (se exige que el solicitante disponga de firma electrónica cualificada o firma ePUAP), sin documentos en papel.',
            'En lugar del sello en el pasaporte (como hasta ahora), el solicitante recibirá (a través de MOS) un certificado de presentación de la solicitud de permiso de residencia.',
            'Dicho certificado confirmará la legalidad de la estancia de la persona extranjera (hasta la finalización firme del procedimiento).',
            'El ministro competente en asuntos internos debe emitir un comunicado oficial sobre la fecha de implantación completa del sistema MOS.',
            'Hasta que lo haga, las solicitudes de residencia se presentarán de la manera actual.',
          ],
        },
        {
          heading: '¿Es el fin de las visitas presenciales a la oficina de la voivodía?',
          paragraphs: [
            'No.',
            'Tras la implantación completa de MOS, la visita seguirá siendo necesaria para:',
            '• la recogida de datos biométricos,',
            '• la verificación de identidad,',
            '• la presentación de una firma de muestra.',
          ],
        },
        {
          heading: '¿La implantación de MOS es un cambio para mejor?',
          paragraphs: [
            'En teoría sí, porque es un paso hacia una administración moderna y digital y hacia la simplificación de los procedimientos de residencia.',
            'En la práctica puede ser menos favorable.',
            'No todas las personas extranjeras tienen acceso a dispositivos informáticos, ni a una firma electrónica cualificada o firma ePUAP (necesaria para presentar una solicitud en MOS).',
            'Además, la persona extranjera puede querer contar con la ayuda de un representante.',
            'Esa ayuda es razonable, ya que las normas sobre legalización de la residencia son complejas.',
            'Sin embargo, MOS exige que la firma la aporte la propia persona extranjera, y no el representante.',
            'Esto no excluye la participación del representante, pero la dificulta.',
            'En resumen, si MOS es un cambio para mejor se verá solo después de su implantación completa.',
          ],
        },
      ],
      ctaTitle: '¿Tienes un asunto de legalización de residencia?',
      ctaText:
        'Si planeas presentar una solicitud de residencia o ya tienes un procedimiento en curso, podemos analizar tu situación y ayudarte a atravesar el trámite con seguridad.',
    },
  },
},

 {
  slug: 'kredyty-frankowe-sytuacja-2026',
  title: 'Kredyty frankowe — sytuacja w 2026 roku',
  category: 'Franki, SKD, WIBOR',
  teaser:
    'Sytuacja frankowiczów pozostaje korzystna. Orzecznictwo TSUE i Sądu Najwyższego daje realne podstawy do dochodzenia zwrotu wpłat uiszczonych na rzecz banków.',
  date: 'styczeń 2026',
  readingTime: '6 min czytania',
  link: '/blog/kredyty-frankowe-sytuacja-2026.html',
  sections: [
    {
      heading: 'Wprowadzenie',
      paragraphs: [
        'Sprawy dotyczące kredytów hipotecznych waloryzowanych do walut obcych, w szczególności kredytów frankowych, od lat budzą duże zainteresowanie w całej Polsce.',
        'W niniejszym artykule przedstawiona zostaje aktualna sytuacja prawna w sprawach dotyczących tych kredytów.',
      ],
    },
    {
      heading: 'Dotychczasowe orzecznictwo',
      paragraphs: [
        'Przełomowym dla spraw związanych z kredytami frankowymi był wyrok Trybunału Sprawiedliwości Unii Europejskiej z dnia 3 października 2019 roku, K. i J. Dziubak przeciwko Raiffeisen Bank International AG (sygn. akt C-260/18).',
        'Trybunał uznał, że zasadne jest ustalenie nieważności umowy kredytu zawierającej abuzywne klauzule waloryzacyjne oraz że sądy krajowe nie mogą samodzielnie uzupełniać luk w umowie kredytu powstałych po wyeliminowaniu takich klauzul.',
        'Równie istotna jest uchwała Sądu Najwyższego z dnia 25 kwietnia 2024 roku (sygn. akt III CZP 25/22), z której wynika zasadność stosowania przez sądy teorii dwóch kondykcji, a także bezzasadność roszczeń banków o wynagrodzenie za korzystanie z kapitału kredytu.',
        'W wyroku z dnia 5 września 2025 roku, sygn. akt II CSKP 550/24, Sąd Najwyższy potwierdził zasadność dalszego stosowania teorii dwóch kondykcji, obalając argumentację banków, że po wyroku Trybunału Sprawiedliwości Unii Europejskiej z dnia 19 czerwca 2025 roku, wydanym w sprawie C-396/24, zasadne jest stosowanie teorii salda.',
        'W rzeczywistości wyrok z dnia 19 czerwca 2025 roku wskazywał jedynie na konieczność uwzględniania przez bank, pozywający kredytobiorcę o zwrot kapitału, wpłat dokonanych przez kredytobiorcę w wykonaniu umowy kredytu, a nie na zasadność stosowania teorii salda.',
        'Istotne znaczenie ma również wyrok Trybunału Sprawiedliwości Unii Europejskiej z dnia 14 grudnia 2023 roku (sygn. akt C-28/22), z którego wynika, że roszczenia o zwrot wpłat kredytobiorcy należy liczyć od momentu, w którym powziął on wiedzę o nieuczciwych warunkach umownych.',
      ],
    },
    {
      heading: 'Co to oznacza dla frankowiczów w praktyce?',
      paragraphs: [
        'Sądy wydają wyroki zasądzające zwrot wszystkich wpłat kredytobiorców uiszczonych w wykonaniu umów kredytu zawierających abuzywne klauzule przeliczeniowe.',
        'Zwrot wpłat kredytobiorców jest zasądzany niezależnie od ewentualnych roszczeń banku.',
        'Kredytobiorcy mają sześć lat na dochodzenie zwrotu uiszczonych wpłat, liczonych od momentu, w którym dowiedzieli się — z wiarygodnego źródła, w szczególności od prawnika, któremu powierzyli swoją sprawę — o nieważności umowy kredytu.',
      ],
    },
    {
      heading: 'Podsumowanie',
      paragraphs: [
        'Frankowicze oraz inni kredytobiorcy posiadający kredyty waloryzowane do walut obcych mają solidne podstawy, oparte na korzystnym orzecznictwie, do dochodzenia zwrotu uiszczonych wpłat.',
        'Pieńkowski – Kancelaria Adwokacka oferuje bezpłatną analizę umów dotyczących kredytów hipotecznych waloryzowanych do walut obcych.',
        'W ramach tej analizy ocenimy, czy te umowy są ważne, zaproponujemy adekwatne do danej sytuacji rozwiązanie (pozew lub ugoda) oraz w sposób jednoznaczny przedstawimy wszystkie koszty związane z prowadzeniem przez nas sprawy.',
      ],
    },
  ],
  ctaTitle: 'Masz kredyt waloryzowany do waluty obcej?',
  ctaText:
    'Jeżeli chcesz odzyskać wpłaty uiszczone w wykonaniu tej umowy, skontaktuj się z naszą kancelarią – ocenimy szanse ich odzyskania, zaproponujemy konkretne rozwiązanie (pozew lub ugoda) oraz jasno zakreślimy wszystkie związane z nim koszty.',
  translations: {
    en: {
      title: 'Swiss franc loans — the situation in 2026',
      teaser:
        'The legal position of Swiss franc borrowers remains favourable. Case law of the CJEU and the Supreme Court provides solid grounds for recovering payments made to banks.',
      sections: [
        {
          heading: 'Introduction',
          paragraphs: [
            'Cases concerning mortgage loans indexed to foreign currencies, in particular Swiss franc loans, have attracted significant attention in Poland for many years.',
            'This article outlines the current legal situation regarding such loans.',
          ],
        },
        {
          heading: 'Existing case law',
          paragraphs: [
            'A landmark ruling for Swiss franc loan cases was the judgment of the Court of Justice of the European Union of 3 October 2019 in K. and J. Dziubak v Raiffeisen Bank International AG (Case C-260/18).',
            'The Court held that declaring a loan agreement containing abusive indexation clauses invalid is justified and that national courts may not fill contractual gaps created after removing such clauses.',
            'Equally important is the Supreme Court resolution of 25 April 2024 (Case III CZP 25/22), confirming the application of the two-condictions theory and rejecting banks’ claims for remuneration for the use of loan capital.',
            'In its judgment of 5 September 2025 (Case II CSKP 550/24), the Supreme Court reaffirmed the continued applicability of the two-condictions theory, rejecting banks’ arguments that the balance theory should apply following the CJEU judgment of 19 June 2025 in Case C-396/24.',
            'In fact, the judgment of 19 June 2025 merely required banks suing borrowers for repayment of capital to take into account payments already made by the borrower, and did not endorse the balance theory.',
            'Also significant is the CJEU judgment of 14 December 2023 (Case C-28/22), which held that limitation periods for borrowers’ claims run from the moment they became aware of the unfair contractual terms.',
          ],
        },
        {
          heading: 'What does this mean for borrowers in practice?',
          paragraphs: [
            'Courts order banks to refund all payments made by borrowers under loan agreements containing abusive indexation clauses.',
            'Such refunds are awarded independently of any claims the bank may assert.',
            'Borrowers have six years to pursue repayment claims, calculated from the moment they learned of the invalidity of the loan agreement from a reliable source, in particular their legal adviser.',
          ],
        },
        {
          heading: 'Summary',
          paragraphs: [
            'Swiss franc borrowers and other holders of foreign-currency-indexed loans have solid grounds, based on favourable case law, to pursue reimbursement of amounts paid.',
            'Pieńkowski – Law Office offers a free analysis of mortgage loan agreements indexed to foreign currencies.',
            'As part of this analysis, we assess the validity of the agreement, propose an appropriate course of action (litigation or settlement), and clearly outline all costs associated with handling the case.',
          ],
        },
      ],
      ctaTitle: 'Do you have a loan indexed to a foreign currency?',
      ctaText:
        'If you wish to recover payments made under such an agreement, contact our law office – we will assess your chances, propose a concrete solution (court proceedings or settlement), and clearly outline all related costs.',
    },
    es: {
      title: 'Préstamos en francos suizos — situación en 2026',
      teaser:
        'La situación de los prestatarios de préstamos en francos suizos sigue siendo favorable. La jurisprudencia del TJUE y del Tribunal Supremo ofrece bases sólidas para recuperar las cantidades pagadas a los bancos.',
      sections: [
        {
          heading: 'Introducción',
          paragraphs: [
            'Los asuntos relativos a préstamos hipotecarios indexados a monedas extranjeras, en particular a francos suizos, llevan años generando un gran interés en Polonia.',
            'Este artículo presenta la situación jurídica actual de dichos préstamos.',
          ],
        },
        {
          heading: 'Jurisprudencia existente',
          paragraphs: [
            'Un fallo clave para los préstamos en francos suizos fue la sentencia del Tribunal de Justicia de la Unión Europea de 3 de octubre de 2019 en el asunto K. y J. Dziubak contra Raiffeisen Bank International AG (asunto C-260/18).',
            'El Tribunal consideró justificada la declaración de nulidad de un contrato de préstamo que contenga cláusulas abusivas de indexación y estableció que los tribunales nacionales no pueden integrar el contrato tras eliminar dichas cláusulas.',
            'Igualmente relevante es la resolución del Tribunal Supremo de 25 de abril de 2024 (asunto III CZP 25/22), que confirma la aplicación de la teoría de las dos condicciones y rechaza las reclamaciones de los bancos por remuneración del capital.',
            'En su sentencia de 5 de septiembre de 2025 (asunto II CSKP 550/24), el Tribunal Supremo confirmó la vigencia de la teoría de las dos condicciones, rechazando la postura de los bancos basada en la sentencia del TJUE de 19 de junio de 2025 (asunto C-396/24).',
            'En realidad, la sentencia de 19 de junio de 2025 solo exige que el banco tenga en cuenta los pagos efectuados por el prestatario al reclamar el capital, y no avala la teoría del saldo.',
            'También es relevante la sentencia del TJUE de 14 de diciembre de 2023 (asunto C-28/22), que establece que el plazo de prescripción comienza cuando el prestatario tiene conocimiento de las cláusulas abusivas.',
          ],
        },
        {
          heading: '¿Qué significa esto en la práctica?',
          paragraphs: [
            'Los tribunales ordenan la devolución de todas las cantidades pagadas por los prestatarios en virtud de contratos con cláusulas abusivas.',
            'La devolución se concede con independencia de las reclamaciones que pueda formular el banco.',
            'Los prestatarios disponen de seis años para reclamar, contados desde el momento en que tuvieron conocimiento de la nulidad del contrato a través de una fuente fiable, en particular su abogado.',
          ],
        },
        {
          heading: 'Resumen',
          paragraphs: [
            'Los prestatarios de préstamos en francos suizos y otros préstamos indexados a monedas extranjeras cuentan con bases sólidas, apoyadas en una jurisprudencia favorable, para reclamar la devolución de las cantidades pagadas.',
            'Pieńkowski – Despacho de Abogados ofrece un análisis gratuito de contratos de préstamos hipotecarios indexados a monedas extranjeras.',
            'Como parte del análisis, evaluamos la validez del contrato, proponemos la solución más adecuada (demanda judicial o acuerdo) y presentamos de forma clara todos los costes asociados al caso.',
          ],
        },
      ],
      ctaTitle: '¿Tienes un préstamo indexado a una moneda extranjera?',
      ctaText:
        'Si deseas recuperar las cantidades pagadas en virtud de dicho contrato, ponte en contacto con nuestro despacho: evaluaremos tus posibilidades, propondremos una solución concreta (demanda o acuerdo) y delimitaremos claramente todos los costes asociados.',
    },
  },
},

  {
    slug: 'krajowy-rejestr-psow-kotow',
    title: 'Strach się bać. Krajowy Rejestr Oznakowanych Psów i Kotów',
    category: 'Paragraf Zwierzoochronny',
    teaser: 'Co to jest i kogo dotyczy Krajowy Rejestr Oznakowanych Psów i Kotów? Jakie zwierzęta będą podlegać obowiązkowemu "oczipowaniu"?',
    date: '11 lipca 2025',
    readingTime: '5 min czytania',
    link: '/blog/krajowy-rejestr-psow-kotow.html',
    featured: false,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/krajowy-rejestr-psow-kotow.png',
      alt: 'KROPIK - Krajowy Rejestr Oznakowanych Psów i Kotów',
    },
    tags: [
      'KROPIK',
      'Krajowy Rejestr Oznakowanych Psów i Kotów',
      'obowiązkowe chipowanie',
      'obowiązek rejestracji w KROPIK',
      'do kiedy trzeba zaczipować psa',
      'do kiedy trzeba zachipować kota',
      'kary za brak chipowania psa',
      'kary za brak chipowania kota',
      'jak znaleźć właściciela psa',
      'jak znaleźć właściciela kota',
      'kto może zmieniać dane w KROPIK',
    ],
    sections: [
      {
        heading: 'REJESTR PSÓW I KOTÓW',
        paragraphs: [
          '**Krajowy Rejestr Oznakowanych Psów i Kotów**, w skrócie **KROPIK**, jest objęty projektem ustawy, który ma zostać przyjęty w II kwartale 2025 roku.',
          '',
          'Za prowadzenie KROPIK-u będzie odpowiadać **Agencja Restrukturyzacji i Modernizacji Rolnictwa**, a cały system funkcjonować ma online.',
          '',
          'Brzmi straszanie, a w głowie roi się od pytań.',
          '',
          'Odpowiedzmy na najważniejsze z nich.',
        ],
      },
      {
        heading: 'PO CO? JAK? DLACZEGO?',
        paragraphs: [
          '**Po co nam Krajowy Rejestr Oznakowanych Psów i Kotów?**',
          '',
          'Najkrócej rzecz ujmując, Krajowy Rejestr Oznakowanych Psów i kotów jest po to, żeby zidentyfikować psa lub kota oraz jego właściciela.',
          '',
          '**Kto będzie miał dostęp do tego rejestru?**',
          '',
          'Rejestr nie będzie ogólnodostępny.',
          '',
          'Prawo do przeglądania danych w KROPIK-u będą mieli:',
        ],
        list: [
          'lekarze weterynarii',
          'gminy',
          'policja',
          'straż gminna (miejska)',
          'prokuratura',
          'sądy',
          'Inspekcja Weterynaryjna – wszyscy ww. w zakresie niezbędnym do realizacji ich ustawowych zadań oraz w celu umożliwienia kontaktu z właścicielem psa lub kota',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: [
          '"Właściciel" zwierzęcia również będzie miał możliwość zalogowania się do systemu — za pośrednictwem aplikacji **mObywatel**.',
          '',
          '**Co będzie mógł zrobić w KROPIK-u właściciel psa lub kota?**',
          '',
          '"Właściciel" będzie mógł w szczególności:',
        ],
        list: [
          'zaktualizować dane zwierzęcia',
          'śledzić terminy wizyt u lekarza weterynarii',
          'otrzymać przypomnienie o obowiązkowych szczepieniach',
          'zgłosić zgubienie lub znalezienia zwierzęcia',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: [
          '**Lekarze weterynarii** również będą mogli modyfikować dane zwierzęcia w KROPIK-u.',
          '',
          '**Jakie zwierzę trzeba zarejestrować w KROPIK-u?**',
          '',
          'Rejestracja w KROPIK-u będzie obowiązkowa dla:',
        ],
        list: [
          'wszystkich psów **urodzonych po dniu wdrożenia systemu** (data zostanie ogłoszona w oficjalnym komunikacie)',
          'psów i kotów wprowadzanych do obrotu lub znajdujących się w schronisku dla zwierząt (również urodzonych przed ww. dniem z komunikatu)',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: ['Pozostałe psy i koty będzie można zarejestrować **dobrowolnie**, na wniosek "właściciela".'],
      },
      {
        heading: 'CO OPRÓCZ KROPIK-u?',
        paragraphs: ['Psy oraz koty będą musiały być "zaczipowane" przez lekarza weterynarii:'],
        list: [
          'psy – **najpóźniej podczas pierwszego obowiązkowego ochronnego szczepienia przeciwko wściekliźnie**',
          'koty – nie później niż **przed ukończeniem 12. miesiąca życia**',
        ],
        listStyle: 'paw',
      },
      {
        heading: '',
        paragraphs: ['Czip musi być wszczepiony **przed wprowadzeniem zwierzęcia do obrotu**.'],
      },
      {
        heading: 'CO, JEŚLI NIE WYPEŁNIĘ OBOWIĄZKÓW?',
        paragraphs: [
          'Nieprzestrzeganie nowych przepisów będzie wiązać się z konsekwencjami.',
          '',
          '**Inspekcja Weterynaryjna** ma otrzymać uprawnienia do **nakładania mandatów karnych** w wysokości od **5 000 zł do 20 000 zł**.',
        ],
      },
      {
        heading: 'SŁOWÓW KILKA NA ZAKOŃCZENIE',
        paragraphs: [
          'Pamiętajcie, że kształt planowanej ustawy może ulec zmianie (a być może nawet wcale nie zostanie uchwalona).',
          '',
          'Dla wielu KROPIK to przykra formalność, lecz może się ona okazać krokiem w stronę lepszego traktowania zwierząt w Polsce.',
          '',
          'Do następnego. 🙂',
        ],
      },
    ],
    relatedSlugs: ['czy-jestem-wlascicielem', 'znecanie-sie-nad-zwierzetami'],
    ctaTitle: 'Masz pytania o prawne konsekwencje wprowadzenia KROPIK-u?',
    ctaText:
      'Skonsultuj się z nami i uninknij przykrych niespodzianek.',
    translations: {
      en: {
        title: 'KROPIK – should you be afraid? The National Register of Marked Dogs and Cats',
        teaser:
          'What is the National Register of Marked Dogs and Cats (KROPIK), who does it cover and which animals will have to be microchipped?',
        sections: [
          {
            heading: 'Register of dogs and cats',
            paragraphs: [
              'The **National Register of Marked Dogs and Cats**, in short **KROPIK**, is part of a draft act that is to be adopted in the second quarter of 2025.',
              '',
              'The register will be managed by the **Agency for Restructuring and Modernisation of Agriculture**, and the whole system is to operate online.',
              '',
              'The name may sound a bit scary and raises a lot of questions.',
              '',
              'Let’s answer the most important of them.',
            ],
          },
          {
            heading: 'Why, how and for what purpose?',
            paragraphs: [
              '**Why do we need a National Register of Marked Dogs and Cats?**',
              '',
              'In short, the register is there to make it possible to identify a dog or cat and its “owner”.',
              '',
              '**Who will have access to the register?**',
              '',
              'The register will not be publicly available.',
              '',
              'The following entities will be able to view the data in KROPIK:',
            ],
            list: [
              'veterinarians,',
              'municipalities,',
              'the police,',
              'municipal (city) guards,',
              'public prosecutors,',
              'courts,',
              'the Veterinary Inspection – all the above mentioned to the extent necessary to carry out its statutory tasks and to enable contact with the dog’s or cat’s “owner”.',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: [
              'The animal’s “owner” will also be able to log into the system – via the **mObywatel** application.',
              '',
              '**What will the dog’s or cat’s “owner” be able to do in KROPIK?**',
              '',
              'In particular they will be able to:',
            ],
            list: [
              'update the animal’s data,',
              'track upcoming veterinary appointments,',
              'receive reminders about mandatory vaccinations,',
              'report a lost or found animal.',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: [
              '**Veterinarians** will also be able to modify animal data in KROPIK.',
              '',
              '**Which animals will have to be registered in KROPIK?**',
              '',
              'Registration in KROPIK will be mandatory for:',
            ],
            list: [
              'all dogs **born after the date on which the system is launched** (the date will be announced in an official communication),',
              'dogs and cats placed on the market or staying in animal shelters (including those born before the date indicated in the communication).',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: [
              'Other dogs and cats will be able to be registered **voluntarily**, at the request of the “owner”.',
            ],
          },
          {
            heading: 'What besides KROPIK?',
            paragraphs: ['Dogs and cats will have to be microchipped by a veterinarian:'],
            list: [
              'dogs – **no later than at the time of the first mandatory rabies vaccination**,',
              'cats – no later than **before they reach 12 months of age**.',
            ],
            listStyle: 'paw',
          },
          {
            heading: '',
            paragraphs: ['The microchip must be implanted **before the animal is placed on the market**.'],
          },
          {
            heading: 'What if you fail to meet your obligations?',
            paragraphs: [
              'Failure to comply with the new rules will involve consequences.',
              '',
              'The **Veterinary Inspection** is to be given powers to impose **fines** ranging from **PLN 5,000 to PLN 20,000**.',
            ],
          },
          {
            heading: 'A few words to conclude',
            paragraphs: [
              'Remember that the final shape of the planned act may still change (and it may even never be adopted).',
              '',
              'For many people KROPIK will feel like an unpleasant formality, but it may also prove to be a step towards better treatment of animals in Poland.',
              '',
              'Till next time. 🙂',
            ],
          },
        ],
    ctaTitle: 'Do you have questions about the legal consequences of introducing KROPIK?',
    ctaText: 'Consult with us and avoid unpleasant surprises.',
  },
es: {
  title: 'KROPIK – ¿hay que tener miedo? Registro Nacional de Perros y Gatos Identificados',
  teaser:
    'Qué es el Registro Nacional de Perros y Gatos Identificados (KROPIK), a quién se aplica y qué animales deberán llevar microchip obligatorio.',
  sections: [
    {
      heading: 'Registro de perros y gatos',
      paragraphs: [
        'El **Registro Nacional de Perros y Gatos Identificados**, abreviado **KROPIK**, forma parte de un proyecto de ley que debería aprobarse en el segundo trimestre de 2025.',
        '',
        'La gestión del registro corresponderá a la **Agencia de Reestructuración y Modernización de la Agricultura**, y todo el sistema funcionará en línea.',
        '',
        'El nombre puede sonar un poco inquietante y suscitar muchas preguntas.',
        '',
        'Intentemos responder a las más importantes.',
      ],
    },
    {
      heading: 'Para qué, cómo y por qué',
      paragraphs: [
        '**¿Para qué sirve el Registro Nacional de Perros y Gatos Identificados?**',
        '',
        'De forma muy resumida, el registro existe para poder identificar a un perro o un gato y a su “propietario”.',
        '',
        '**¿Quién tendrá acceso al registro?**',
        '',
        'El registro no será de acceso público.',
        '',
        'Podrán consultar los datos en KROPIK, entre otros:',
      ],
      list: [
        'personas veterinarias,',
        'ayuntamientos,',
        'la policía,',
        'guardias municipales,',
        'fiscalía,',
        'tribunales,',
        'la Inspección Veterinaria – todos los mencionados - en la medida necesaria para el desempeño de sus funciones legales y para poder contactar con el “propietario” del perro o gato.',
      ],
      listStyle: 'paw',
    },
    {
      heading: '',
      paragraphs: [
        'El “propietario” del animal también podrá acceder al sistema a través de la aplicación **mObywatel**.',
        '',
        '**¿Qué podrá hacer el “propietario” del perro o gato en KROPIK?**',
        '',
        'En particular podrá:',
      ],
      list: [
        'actualizar los datos del animal,',
        'controlar las próximas visitas veterinarias,',
        'recibir recordatorios de las vacunas obligatorias,',
        'comunicar la pérdida o el hallazgo de un animal.',
      ],
      listStyle: 'paw',
    },
    {
      heading: '',
      paragraphs: [
        'Las **personas veterinarias** también podrán modificar los datos del animal en KROPIK.',
        '',
        '**¿Qué animales deberán registrarse en KROPIK?**',
        '',
        'La inscripción en KROPIK será obligatoria para:',
      ],
      list: [
        'todos los perros **nacidos después de la fecha de puesta en marcha del sistema** (fecha que se anunciará mediante comunicación oficial),',
        'perros y gatos puestos en el mercado o alojados en refugios para animales (también nacidos antes de la fecha indicada en dicha comunicación).',
      ],
      listStyle: 'paw',
    },
    {
      heading: '',
      paragraphs: [
        'Los demás perros y gatos podrán inscribirse **voluntariamente**, a petición del “propietario”.',
      ],
    },
    {
      heading: 'Qué hay además de KROPIK',
      paragraphs: [
        'Perros y gatos deberán ser identificados mediante microchip por una persona veterinaria:',
      ],
      list: [
        'los perros – **a más tardar en el momento de la primera vacuna antirrábica obligatoria**,',
        'los gatos – como máximo **antes de cumplir los 12 meses de edad**.',
      ],
      listStyle: 'paw',
    },
    {
      heading: '',
      paragraphs: ['El microchip deberá implantarse **antes de poner al animal en el mercado**.'],
    },
    {
      heading: 'Qué ocurre si no cumples tus obligaciones',
      paragraphs: [
        'El incumplimiento de las nuevas normas conllevará consecuencias.',
        '',
        'Se prevé otorgar a la **Inspección Veterinaria** la facultad de imponer **multas** de entre **5.000 y 20.000 PLN**.',
      ],
    },
    {
      heading: 'Unas palabras para terminar',
      paragraphs: [
        'Recuerda que el contenido definitivo de la ley aún puede cambiar (e incluso puede ocurrir que finalmente no se apruebe).',
        '',
        'Para muchas personas KROPIK puede parecer una formalidad molesta, pero también puede convertirse en un paso hacia un mejor trato de los animales en Polonia.',
        '',
        'Hasta la próxima. 🙂',
      ],
    },
  ],
    ctaTitle: '¿Tienes dudas sobre las consecuencias legales de la introducción de KROPIK?',
    ctaText: 'Consulta con nosotros y evita sorpresas desagradables.',
  },
},
},
{
  slug: 'psi-los-w-iranie',
  title: 'To nie jest kraj dla psów (oraz ich właścicieli). Psi los w Iranie.',
  category: 'Paragraf Zwierzoochronny',
    teaser: 'Jak wygląda życie psów i ich właścicieli w Iranie? Czy za wyprowadzenie psa na spacer grozi chłosta?',
    date: '24 czerwca 2025',
    readingTime: '4 min czytania',
    link: '/blog/psi-los-w-iranie.html',
    featured: false,
    isParagrafZwierzoochronny: true,
    featuredImage: {
      src: '/blog-images/psi-los-w-iranie.jpg',
      alt: 'Pies w Iranie - ilustracja sytuacji czworonogów w tym kraju',
    },
    tags: [
      'ciekawostki o psach',
      'czy mogę mieć psa w Iranie',
      'kary za posiadanie psa w Iranie',
      'pies w Iranie',
      'zakaz posiadania psów',
      'flogging za psa',
      'prawo zwierząt na świecie',
    ],
    sections: [
  {
    heading: 'CHŁOSTA',
    paragraphs: [
      'Barzin podnosi wzrok na kata.',
      '',
      'Zostały tylko 3 baty i będzie mógł wrócić do domu.',
      '',
      'Wśród gapiów rozchodzi się szept:',
      '',
      '„Barzin znowu wyprowadził psa na spacer”.',
      '',
      'Że co? Że jak? – zapytasz, drogi czytelniku.',
      '',
      'Właśnie tak.',
      '',
      'Taki jest psi los w Iranie.',
      '',
      'A raczej – los psów i ich właścicieli.',
    ],
  },
  {
    heading: 'PIES - ZABRONIĆ, ŚCIGAĆ, UKARAĆ!',
    paragraphs: [
      'W 19 miastach Iranu obowiązuje zakaz wyprowadzania psów.',
      '',
      'Oficjalnie – z powodów sanitarnych i kulturowych.',
      '',
      'Nieoficjalnie – bo pies to symbol „zgniłego Zachodu”.',
      '',
      'Policja (nomen omen, kolokwialnie nazywana w Polsce „psami”) węszy, gmera, a gdy wywęszy i wygmera – wyciąga konsekwencje (co najmniej mandat).',
      '',
      'Mimo zakazów właściciele psów nie rezygnują.',
      '',
      'Przemykają nocami wraz ze swoimi czworonożnymi przyjaciółmi wąskimi, słabo oświetlonymi uliczkami – byle tylko nie dać się złapać.',
      '',
      'Jeżeli – o zgrozo – przyjdzie im do głowy przewożenie psa samochodem (czego dotyczy inny zakaz), również muszą zachować szczególną ostrożność (najlepiej przykryć psa kocem i poprosić, żeby nie szczekał).',
    ],
  },
  {
    heading: 'CO W KOŃCU Z TYM BATOŻENIEM?',
    paragraphs: [
      'To jeszcze nie rzeczywistość.',
      '',
      'Na razie.',
      '',
      'Ale pomysł chłosty jako kary za wyprowadzanie psa był naprawdę rozważany.',
      '',
      'Nie zdziwmy się zatem, jeśli pewnego dnia Barzinowi rzeczywiście zostaną wymierzone baty.',
      '',
      'Wszystko to dzieje się tu i teraz.',
      '',
      'I choć te zakazy brzmią jak ponury żart – są realiami, z którymi mierzą się Irańczycy.',
    ],
  },
  {
    heading: 'SŁOWÓW KILKA NA ZAKOŃCZENIE',
    paragraphs: [
      'Daj znać, jeżeli chcesz więcej takich ciekawostek!',
      '',
      'Do następnego. 😊',
    ],
  },
],
relatedSlugs: ['znecanie-sie-nad-zwierzetami', 'czy-jestem-wlascicielem'],
ctaTitle: 'Potrzebujesz wsparcia w sprawie dotyczącej praw zwierząt?',
ctaText:
  'Skontaktuj się z nami, a porozmawiamy o możliwych krokach prawnych.',
translations: {
  en: {
    title: 'This is not a country for dogs (or their guardians). The fate of dogs in Iran',
    teaser:
      'What is life like for dogs and their guardians in Iran? About bans, penalties and the situation of four-legged friends in a country where owning a dog can lead to flogging.',
    sections: [
      {
        heading: 'Flogging',
        paragraphs: [
          'Barzin raises his eyes to the executioner.',
          '',
          'Only three lashes remain before he can go home.',
          '',
          'A whisper spreads among the onlookers:',
          '',
          '“Barzin walked his dog again.”',
          '',
          'What? How? – you may ask, dear reader.',
          '',
          'Exactly.',
          '',
          'This is the fate of dogs in Iran.',
          '',
          'Or rather – the fate of dogs and their guardians.',
        ],
      },
      {
        heading: 'A dog – ban, pursue, punish',
        paragraphs: [
          'In 19 Iranian cities, walking dogs is prohibited.',
          '',
          'Officially – for sanitary and cultural reasons.',
          '',
          'Unofficially – because a dog is seen as a symbol of the “decadent West”.',
          '',
          'The police sniff around, search, and once they find something – they impose consequences (at the very least, a fine).',
          '',
          'Despite the bans, dog guardians do not give up.',
          '',
          'They sneak out at night with their four-legged friends through narrow, poorly lit streets – just to avoid being caught.',
          '',
          'If – heaven forbid – they decide to transport a dog by car (which is also prohibited), they must be extra cautious (preferably cover the dog with a blanket and hope it stays silent).',
        ],
      },
      {
        heading: 'So what about flogging?',
        paragraphs: [
          'This is not yet reality.',
          '',
          'For now.',
          '',
          'But the idea of flogging as a punishment for walking a dog was seriously considered.',
          '',
          'So let us not be surprised if one day Barzin actually receives those lashes.',
          '',
          'All of this is happening here and now.',
          '',
          'And although these bans sound like a grim joke, they are the everyday reality faced by Iranians.',
        ],
      },
      {
        heading: 'A few words to conclude',
        paragraphs: [
          'Let me know if you would like more stories like this!',
          '',
          'Until next time. 😊',
        ],
      },
    ],
    ctaTitle: 'Do you need support in an animal law matter?',
    ctaText:
      'Contact us and we will discuss the possible legal steps.',
  },
  es: {
    title: 'Este no es un país para perros (ni para sus cuidadores). El destino de los perros en Irán',
    teaser:
      '¿Cómo es la vida de los perros y de sus cuidadores en Irán? Sobre prohibiciones, sanciones y la situación de los animales en un país donde tener un perro puede acabar en azotes.',
    sections: [
      {
        heading: 'Flagelación',
        paragraphs: [
          'Barzin levanta la vista hacia el verdugo.',
          '',
          'Solo quedan tres latigazos para poder volver a casa.',
          '',
          'Entre los curiosos se extiende un susurro:',
          '',
          '“Barzin volvió a sacar al perro a pasear”.',
          '',
          '¿Qué? ¿Cómo? – te preguntarás, querido lector.',
          '',
          'Así es.',
          '',
          'Este es el destino de los perros en Irán.',
          '',
          'O más bien – el destino de los perros y de sus cuidadores.',
        ],
      },
      {
        heading: 'Perro – prohibir, perseguir, castigar',
        paragraphs: [
          'En 19 ciudades de Irán está prohibido pasear perros.',
          '',
          'Oficialmente – por motivos sanitarios y culturales.',
          '',
          'Extraoficialmente – porque el perro es visto como un símbolo del “Occidente decadente”.',
          '',
          'La policía husmea, registra y, cuando encuentra algo, impone consecuencias (como mínimo, una multa).',
          '',
          'A pesar de las prohibiciones, los cuidadores de perros no se rinden.',
          '',
          'Se escabullen de noche con sus amigos de cuatro patas por calles estrechas y mal iluminadas, con tal de no ser atrapados.',
          '',
          'Si – Dios no lo quiera – se les ocurre transportar al perro en coche (lo cual también está prohibido), deben extremar la precaución (lo mejor es cubrir al perro con una manta y esperar que no ladre).',
        ],
      },
      {
        heading: '¿Y qué pasa finalmente con los azotes?',
        paragraphs: [
          'Todavía no es una realidad.',
          '',
          'Por ahora.',
          '',
          'Pero la idea de la flagelación como castigo por pasear a un perro fue seriamente considerada.',
          '',
          'No nos sorprendamos si algún día Barzin realmente recibe los azotes.',
          '',
          'Todo esto ocurre aquí y ahora.',
          '',
          'Y aunque estas prohibiciones suenan como una broma macabra, son la realidad con la que se enfrentan los iraníes.',
        ],
      },
      {
        heading: 'Unas palabras para terminar',
        paragraphs: [
          '¡Avísame si quieres más historias como esta!',
          '',
          'Hasta la próxima. 😊',
        ],
      },
    ],
    ctaTitle: '¿Necesitas apoyo en un asunto relacionado con los derechos de los animales?',
    ctaText:
      'Ponte en contacto con nosotros y hablaremos sobre las posibles acciones legales.',
  },
},

  },
];

const pl: Content = {
  nav: [
    { label: 'Start', href: 'hero', type: 'anchor' },
    { label: 'O nas', href: '/o-nas', type: 'path' },
    { label: 'Obszary praktyki', href: 'specjalizacje', type: 'anchor' },
    { label: 'Blog', href: '/blog', type: 'path' },
    { label: 'Kontakt', href: 'kontakt', type: 'anchor' },
  ],
  hero: {
    id: 'hero',
    eyebrow: 'Pieńkowski – Kancelaria Adwokacka • Warszawa',
    title: 'Prawo po ludzku, w trzech językach.',
    lead: [
      'Nazywam się Piotr Pieńkowski, jestem adwokatem, założycielem kancelarii Pieńkowski – Kancelaria Adwokacka.',
      'Nasza kancelaria pomaga w sprawach cywilnych, w tym dotyczących kredytów "frankowych", kredytów waloryzowanych WIBOR-em oraz kredytów objętych sankcją kredytu darmowego, w legalizacji pobytu cudzoziemców na terytorium RP oraz we wszelkich sprawach dotyczących praw zwierząt.',
'Co nas wyróżnia:',
    ],
    bullets: [
  'indywidualne podejście i jasne zasady współpracy,',
  'wieloletnie doświadczenie zdobyte w kancelariach prawnych różnej wielkości,',
  'pełna obsługa po polsku, angielsku i hiszpańsku.',
],
    ctas: {
      primary: { label: 'Umów konsultację', href: '#kontakt' },
      secondary: { label: 'Zadzwoń teraz', href: 'tel:+48889054555' },
    },
    portraitAlt: 'Adwokat Piotr Pieńkowski – portret',
  },
  forWhom: {
    id: 'dla-kogo',
    title: 'Komu pomagamy?',
    intro:
      '',
    cards: [
       {
        title: 'Kredytobiorcom',
        description:
          'Pomagamy kredytobiorcom w sporach z bankami – w szczególności w sprawach kredytów waloryzowanych do CHF albo EUR, kredytów z WIBOR oraz w sprawach dotyczących sankcji kredytu darmowego.',
      },
	{
        title: 'Cudzoziemcom',
        description:
          'Wspieramy cudzoziemców w postępowaniach administracyjnych oraz sądowoadministracyjnych, dotyczących legalizacji pobytu i pracy w Polsce. Komunikujemy się płynnie po angielsku i hiszpańsku.',
      },
     {
        title: 'Przedsiębiorcom',
        description:
          'Pomagamy w założeniu działalności gospodarczej, przygotowujemy umowy oraz zastępujemy klientów przed sądami powszechnymi, przed sądami polubownymi lub w mediacji.',
      },
      {
        title: 'Innym podmiotom',
        description:
          'Pomagamy w sprawach cywilnych oraz w sprawach dotyczących praw zwierząt, w tym o zapłatę odszkodowania (np. w związku z pogryzieniem lub błędem weterynaryjnym), zadośćuczynienia, naruszenia dóbr osobistych, w sprawach o znęcanie się nad zwierzętami, a także w sprawach rodzinnych – m.in. rozwodach, alimentach oraz kontaktach z dziećmi.',
      },
    ],
    linkText:
      'Nie widzisz tu swojej sytuacji? Napisz – sprawdzę, jak mogę pomóc.',
  },
  specialisations: {
    id: 'specjalizacje',
    title: 'Obszary praktyki',
    intro:
      'Oferujemy kompleksową pomoc – od jednorazowych konsultacji, umów, opinii prawnych, aż po zastępstwo przed sądami lub organami administracji publicznej',
    items: [
      {
        title: 'Prawa zwierząt',
        description:
          'Prowadzimy sprawy m.in. o znęcanie się nad zwierzętami, o odszkodowanie lub zadośćuczynienie za pogryzienie, dotyczące błędów weterynaryjnych, dotyczące rękojmi za "wady" zwierząt.',
      },
      {
        title: 'Kredyty walutowe',
        description:
          'Prowadzimy sprawy dotyczące kredytów indeksowanych albo denominowanych do walut obcych (CHF, EUR).',
      },
      {
        title: 'Sankcja kredytu darmowego',
        description:
          'Pomagamy konsumentom dochodzić sankcji kredytu darmowego, gdy bank nie wypełnił swoich obowiązków, wynikających z ustawy o kredycie konsumenckim z dnia 12 maja 2011 roku, Dz.U. 2011 nr 126 poz. 715.',
      },
      {
        title: 'Kredyty z WIBOR',
        description:
          'Pomagamy kredytobiorcom w sprawach dotyczących umów opartych o wskaźnik WIBOR, w tym w sprawach o ustalenie nieważności ww. umów lub o zwrot nadpłaconych rat kredytu.',
      },
   {
        title: 'Legalizacja pobytu',
        description:
          'Pomagamy w legalizacji pobytu cudzoziemców na terytorium RP, w postępowaniu przed wojewodą oraz w postępowaniu odwoławczym.',
      },
      {
        title: 'Obsługa przedsiębiorstw',
        description:
          'Pomagamy w zarejestrowaniu Twojej działalności gospodarczej, w sporządzaniu umów, uchwał, a także w rozwiązywaniu sporów, zarówno na drodze sądowej, jak i przy wykorzystaniu pozasądowych metod.',
      },
      {
        title: 'Upadłość konsumencka',
        description:
          'Pomagamy osobom zadłużonym w postępowaniu o ogłoszenie upadłości konsumenckiej i w postępowaniu po ogłoszeniu ww. upadłości.',
      },
      {
        title: 'Prawo cywilne',
        description:
          'Prowadzimy m.in. sprawy o zapłatę należności z faktur, odszkodowanie, zadośćuczynienie lub dotyczące rękojmi za wady rzeczy sprzedanej.',
      },
      {
        title: 'Prawo spadkowe',
        description:
          'Zajmujemy się sprawami dotyczącymi dziedziczenia, zachowku, działu spadku oraz odpowiedzialności za długi spadkowe.',
      },
      {
        title: 'Prawo rodzinne',
        description:
          'Reprezentujemy klientów w sprawach rozwodowych, alimentacyjnych, dotyczących władzy rodzicielskiej oraz kontaktów z dziećmi.',
      },
      {
        title: 'Umowy i opinie prawne',
        description:
          'Przygotowujemy i analizujemy umowy oraz sporządzamy opinie prawne, w sprawach z zakresu naszej praktyki.',
      },
      {
        title: 'Reprezentacja w sądzie',
        description:
          'Zastępujemy klientów przed sądami powszechnymi, Sądem Najwyższym, sądami administracyjnymi, przed sądami polubownymi (oraz w mediacji).',
      },
    ],
  },
  whyMe: {
    id: 'dlaczego',
    title: 'Dlaczego warto z nami współpracować?',
    lead:
      '',
    usps: [
      {
        title: 'Jasne zasady współpracy',
        description:
          'Przed nawiązaniem współpracy informujemy klienta o wszystkich kosztach, ryzykach oraz możliwych rozwiązaniach problemu klienta.',
      },
      {
        title: 'Stały kontakt',
        description:
          'Na bieżąco zawiadamiamy o wszelkich zmianach w sprawie, odbieramy telefony, odpisujemy na maile lub wiadomości na Whatsapp.',
      },
 {
        title: 'Indywidualne podejście',
        description:
          'Każdą sprawę traktujemy indywidualnie. Zależy nam na znalezieniu rozwiązania, które realnie pomaga klientowi.',
      }, {
        title: 'Doświadczenie i zaangażowanie',
        description:
          'Mamy wieloletnie doświadczenie zdobyte w kancelariach prawnych oraz korporacjach, rozumiemy biznesowe tło spraw naszych klientów, mówimy w trzech językach i w pełni angażujemy się w prowadzone sprawy.',
      },
    ],
  },
  aboutProfessional: {
    id: 'o-nas',
    title: 'O nas',
    paragraphs: [
      'Pieńkowski – Kancelaria Adwokacka została założona przez adwokata Piotra Pieńkowskiego, absolwenta prawa na Uniwersytecie Warszawskim oraz Międzynarodowych Stosunków Gospodarczych w Szkole Głównej Handlowej w Warszawie.',
      'Połączenie prawa i ekonomii pozwala lepiej zrozumieć biznesowe tło spraw naszych klientów.',
      'Do każdej sprawy podchodzimy z pasją i zaangażowaniem – niezależnie od tego, czy dotyczy ona ustalenia nieważności umowy kredytu, sporządzenia uchwał w spółce z o.o., legalizacji pobytu na terytorium RP, czy praw zwierząt, np. roszczenia o zapłatę w związku z pogryzieniem przez psa.',
      'Wieloletnie doświadczenie, zdobyte w kancelariach prawnych oraz korporacjach, umożliwia nam znalezienie realnych rozwiązań ww. spraw.',
'Szukamy ich z uwzględnieniem niuansów sytuacji klienta, bez automatyzmu, proponując klientowi to, co naszym zdaniem jest dla niego najbardziej korzystne.',
'Serdecznie zapraszamy do współpracy!',
    ],
    blogCtaText: 'Zobacz mój blog o prawach zwierząt – Paragraf Zwierzoochronny',
    blogCtaLink: 'https://paragrafzwierzoochronny.pl',
  },
  aboutPrivate: {
    id: 'prywatnie',
    title: 'Prywatnie',
    paragraphs: [
      'Poza pracą zawodową dużo podróżuję – lubię odkrywać nowe miejsca i kultury. W wolnym czasie sięgam po dobrą literaturę, gram na gitarze i zajmuję się fotografią.',
      'Te pasje pomagają mi patrzeć szerzej na świat i lepiej rozumieć historie osób, z którymi pracuję.',
    ],
  },
  process: {
    id: 'jak-pracuje',
    title: 'Jak wygląda współpraca z nami?',
    steps: [
      {
        title: 'Kontakt',
        description:
          'Zaczynamy od krótkiego kontaktu telefonicznego, mailowego lub przez Whatsapp, w trakcie którego wstępnie omawiamy problem i sprawdzamy, w czym możemy pomóc.',
      },
      {
        title: 'Analiza sprawy',
        description:
          'Proponujemy, jak można rozwiązać Twój problem, wskazując potencjalne ryzyka oraz szanse powodzenia.',
      },
      {
        title: 'Ustalenie zasad współpracy',
        description:
          'Ustalamy zakres naszej pomocy oraz sposób rozliczenia – warunki współpracy są zawsze przejrzyste i dopasowane do Twojej sytuacji.',
      },
      {
        title: 'Prowadzenie sprawy',
        description:
          'Pomagamy, prowadząc negocjacje, sporządzając opinie prawne, reprezentując Ciebie przed sądami lub organami administracji publicznej. Zawsze na bieżąco informujemy o jakichkolwiek zmianach w Twojej sprawie.',
      },
    ],
  },
  testimonials: {
    id: 'opinie',
    items: [
      {
        quote:
          'Bardzo rzetelne podejście do sprawy, jasna komunikacja i spokojne tłumaczenie wszystkich etapów postępowania.',
        author: 'Klient indywidualny – sprawa dotycząca kredytu walutowego',
      },
      {
        quote:
          'Profesjonalne wsparcie i realne zaangażowanie. Czułem, że moja sprawa jest w dobrych rękach.',
        author: 'Klient biznesowy',
      },
    ],
  },
  blog: {
    teaser: {
      id: 'blog-teaser',
      title: 'Blog',
      subtitle: 'Przewodnik po prawach zwierząt, kredytach frankowych, Wiborze, SKD oraz legalizacji pobytu',
      ctaLabel: 'Zobacz wszystkie wpisy',
      ctaLink: '/blog',
      featuredSlugs: [
        'kredyty-frankowe-sytuacja-2026',
        'znecanie-sie-nad-zwierzetami',
	'rewolucja-w-legalizacji-pobytu-mos-2026',
	'rekojmia-za-wady-zwierzecia',
      ],
    },
    posts: blogPosts,
  },
  contact: {
    id: 'kontakt',
    title: 'Kontakt',
    officeHeading: 'Dane kancelarii',
    details: [
      {
        label: 'e-mail',
        value: 'biuro@pienkowskilegal.pl',
        href: 'mailto:biuro@pienkowskilegal.pl',
      },
      {
        label: 'telefon',
        value: '+48 889 054 555',
        href: 'tel:+48889054555',
      },
      {
        label: 'adres',
        value: 'ul. Bracka 20/7a, 00-028 Warszawa',
      },
      {
        label: 'godziny otwarcia',
        value: 'poniedziałek–piątek, 9:00–17:00',
      },
    ],
    helper:
      'Chcesz przyśpieszyć nasz kontakt? Opisz nam krótko (w kilku zdaniach) Twój problem, korzystając z formularza.',
    fields: [
      { name: 'name', label: 'Imię i nazwisko', required: true },
      { name: 'email', label: 'E-mail', type: 'email', required: true },
      { name: 'subject', label: 'Temat', required: true },
      { name: 'message', label: 'Treść wiadomości', required: true },
    ],
    consentLabel: 'Zapoznałem(am) się z ',
consentLinkLabel: 'polityką prywatności',
submitLabel: 'Wyślij wiadomość',
  },
  footer: {
    copyright: '© 2026 Pieńkowski – Kancelaria Adwokacka. Wszystkie prawa zastrzeżone.',
    privacy: 'Polityka prywatności i cookies',
    cookiesSettings: 'Ustawienia cookies',
    terms: 'Regulamin serwisu',
    socials: [
      { name: 'LinkedIn', href: 'https://www.linkedin.com/in/piotr-pieńkowski-1b56632a1/' },
      { name: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61580995414859' },
    ],
  },
};

const en: Content = {
  ...pl,
  nav: [
    { label: 'Start', href: 'hero', type: 'anchor' },
    { label: 'About us', href: '/about-me', type: 'path' },
    { label: 'Practice areas', href: 'specjalizacje', type: 'anchor' },
    { label: 'Blog', href: '/blog', type: 'path' },
    { label: 'Contact', href: 'kontakt', type: 'anchor' },
  ],
  hero: {
    ...pl.hero,
    eyebrow: 'Pieńkowski – Law Office • Warsaw',
    title: 'Law made human, in three languages.',
    lead: [
      'My name is Piotr Pieńkowski. I am an attorney-at-law and the founder of Pieńkowski – Law Office.',
      'Our law office provides legal assistance in civil matters, including cases concerning foreign currency loans (CHF), loans indexed to WIBOR, loans subject to the free credit sanction, legalisation of stay of foreign nationals in Poland, as well as all matters related to animal rights.',
'What sets us apart:'
    ],
    bullets: [
'individual approach and clear terms of cooperation,',  
'full support in Polish, English and Spanish,',    
'experience gained in law firms and corporate environments.',
    ],
    ctas: {
      primary: { label: 'Book a consultation', href: '#kontakt' },
      secondary: { label: 'Call now', href: 'tel:+48889054555' },
    },
    portraitAlt: 'Attorney Piotr Pieńkowski – portrait',
  },
  forWhom: {
    ...pl.forWhom,
    title: 'Who we help?',
    intro:
      '',
    cards: [
 {
        title: 'Borrowers',
        description:
          'We assist borrowers in disputes with banks, in particular in cases concerning CHF/EUR loans, WIBOR-based loans and the free credit sanction.',
      },      
{
        title: 'Foreign nationals',
        description:
          'We assist foreign nationals in administrative and administrative court proceedings concerning legalisation of stay and work in Poland. We communicate fluently in English and Spanish.',
      },
 {
        title: 'Entrepreneurs',
        description:
          'We assist in establishing business activity, preparing contracts and representing clients before courts, arbitration tribunals and in mediation.',
      },
      {
        title: 'Other clients',
        description:
          'We assist in civil cases and animal rights cases, including claims for damages and compensation (e.g. dog bites, veterinary malpractice), protection of personal rights, animal abuse cases and family matters such as divorce, maintenance and contact with children.',
      },
    ],
    linkText:
      "You don’t see your situation here? Write to me – I will check how I can help.",
  },
  specialisations: {
    ...pl.specialisations,
    title: 'Practice Areas',
    intro:
      'We provide comprehensive legal support – from one-off consultations, contracts and legal opinions, to representation before courts or public authorities',

    items: [
      {
        title: 'Animal rights',
        description:
          'We handle cases concerning animal rights, including cases of animal abuse, claims for damages or compensation for injuries (e.g. dog bites), veterinary malpractice and warranty claims related to the sale of animals.',
      },
      {
        title: 'Foreign currency loans (CHF / EUR)',
        description:
          'We represent clients in disputes concerning loans indexed or denominated in foreign currencies (CHF, EUR).',
      },
      {
        title: 'Free credit sanction',
        description:
          'We assist consumers in asserting the free credit sanction where banks have failed to comply with their statutory obligations under the Polish Consumer Credit Act of 12 May 2011.',
      },
      {
        title: 'WIBOR loans',
        description:
          'We assist borrowers in matters related to loans based on the WIBOR index, including proceedings aimed at declaring loan agreements invalid or recovering overpaid instalments.',
      },
   {
        title: 'Legalisation of stay',
        description:
          'We assist foreign nationals with the legalisation of their stay in Poland, including proceedings before the voivode and appeal proceedings.',
      },
      {
        title: 'Business services',
        description:
          'We assist in establishing business activity, preparing contracts and resolutions, as well as representing clients in disputes before common courts, arbitration courts and in mediation.',
      },
      {
        title: 'Consumer insolvency',
        description:
          'We assist indebted individuals in consumer insolvency proceedings, as well as in proceedings conducted after the declaration of insolvency.',
      },
      {
        title: 'Civil law',
        description:
          'We handle, among others, claims for payment of receivables arising from invoices, claims for damages and compensation, as well as matters concerning statutory warranty for defects of sold goods.',
      },
      {
        title: 'Inheritance law',
        description:
          'We handle matters related to inheritance, statutory shares of inheritance (legitime), division of an estate and liability for inheritance debts.',
      },
      {
        title: 'Family law',
        description:
          'We assist in family law matters, including divorce proceedings, maintenance claims and cases concerning contact with children.',
      },
      {
        title: 'Contracts and legal opinions',
        description:
          'We prepare and analyse contracts and draft legal opinions within the areas of our legal practice.',
      },
      {
        title: 'Court representation',
        description:
          'We represent clients before common courts, the Supreme Court, administrative courts, arbitration courts and in mediation proceedings.',
      },
    ],
  },
  whyMe: {
    ...pl.whyMe,
    title: 'Why work with us?',
    lead:
      '',
    usps: [
      {
        title: 'Clear terms of cooperation',
        description:
          'Before starting cooperation, we inform the client about all costs, risks and possible solutions.',
      },
      {
        title: 'Ongoing communication',
        description:
          'We keep our clients informed of all developments and remain available by phone, email and WhatsApp.',
      }, 
      {
        title: 'Individual approach',
        description:
          'Each case is treated individually – we focus on solutions that genuinely benefit the client.',
      },
 {
        title: 'Experience and commitment',
        description:
          'We have extensive experience gained in law firms and corporations, understand the business context of legal matters, work in three languages and fully engage in our clients’ cases.',
      },
    ],
  },
  aboutProfessional: {
    ...pl.aboutProfessional,
    title: 'About us',
    paragraphs: [
      'Pieńkowski – Law Office was founded by attorney-at-law Piotr Pieńkowski, a graduate of law at the University of Warsaw and International Business at the Warsaw School of Economics.',
      'The combination of legal and economic education allows us to better understand the business background of our clients’ cases.',
      'We approach each matter with commitment and passion – whether it concerns the invalidation of a loan agreement, preparation of corporate resolutions, legalisation of stay in Poland or claims arising from a dog bite.',
      'Our experience gained in law firms and corporations enables us to find practical and effective solutions, always tailored to the individual situation of the client.',
'We warmly invite you to cooperate with us.'
    ],
    blogCtaText: 'See my animal law blog – Zooprotection Paragraph',
  },
  aboutPrivate: {
    ...pl.aboutPrivate,
    title: 'Outside of work',
    paragraphs: [
      'Outside of my professional practice I travel a lot – I enjoy discovering new places and cultures. In my free time I reach for good literature, play the guitar and pursue photography.',
      'These passions help me look at the world more broadly and better understand the stories of the people I work with.',
    ],
  },
  process: {
    ...pl.process,
    title: 'How cooperation works',
    steps: [
      {
        title: 'Initial contact',
        description:
          'We start with contact by phone, email or WhatsApp.',
      },
      {
        title: 'Case analysis',
        description:
          'We analyse the matter and determine how we can assist.',
      },
      {
        title: 'Terms of cooperation',
        description:
          'We agree on the scope of our assistance and the method of settlement – the terms of cooperation are always transparent and tailored to your situation.',
      },
      {
        title: 'Handling the case',
        description:
          'We conduct negotiations, prepare legal opinions, represent you before courts or public authorities and keep you informed of all developments.',
      },
    ],
  },
  testimonials: {
    ...pl.testimonials,
    items: [
      {
        quote:
          'Very thorough approach to the case, clear communication and calm explanation of each stage of the proceedings.',
        author: 'Individual client – foreign currency loan case',
      },
      {
        quote:
          'Professional support and genuine commitment. I felt my case was in good hands.',
        author: 'Business client',
      },
    ],
  },
  blog: {
    ...pl.blog,
    teaser: {
      ...pl.blog.teaser,
      title: 'Blog',
      subtitle:
        'Guides on animal rights, foreign currency loans, WIBOR loans, free credit sanction and legalisation of stay in Poland',
      ctaLabel: 'See all articles',
    },
  },
  contact: {
    ...pl.contact,
    title: 'Contact',
    officeHeading: 'Law firm details',
    details: [
      { label: 'e-mail', value: 'biuro@pienkowskilegal.pl', href: 'mailto:biuro@pienkowskilegal.pl' },
      { label: 'phone', value: '+48 889 054 555', href: 'tel:+48889054555' },
      { label: 'address', value: 'ul. Bracka 20/7a, 00-028 Warsaw, Poland' },
      { label: 'hours', value: 'Monday–Friday, 9:00–17:00 (CET)' },
    ],
    helper:
      'Would you like to speed up our communication? Please briefly describe your issue (in a few sentences) using the form.',
    fields: [
      { name: 'name', label: 'Full name', required: true },
      { name: 'email', label: 'E-mail', type: 'email', required: true },
      { name: 'subject', label: 'Subject', required: true },
      { name: 'message', label: 'Message', required: true },
    ],
    consentLabel: 'I have read the ',
consentLinkLabel: 'privacy policy',
submitLabel: 'Send message',

  },
  footer: {
    ...pl.footer,
    copyright: '© 2026 Pieńkowski – Law Office. All rights reserved.',
    privacy: 'Privacy policy and cookies',
    cookiesSettings: 'Cookie settings',
    terms: 'Terms of service',
  },
};

const es: Content = {
  ...pl,
  nav: [
    { label: 'Inicio', href: 'hero', type: 'anchor' },
    { label: 'Sobre nosotros', href: '/sobre-mi', type: 'path' },
    { label: 'Áreas de práctica', href: 'specjalizacje', type: 'anchor' },
    { label: 'Blog', href: '/blog', type: 'path' },
    { label: 'Contacto', href: 'kontakt', type: 'anchor' },
  ],
  hero: {
    ...pl.hero,
    eyebrow: 'Pieńkowski – Despacho de Abogados • Varsovia',
    title: 'Derecho en lenguaje humano, en tres idiomas.',
    lead: [
      'Mi nombre es Piotr Pieńkowski. Soy abogado y fundador del despacho Pieńkowski – Despacho de Abogados.',
'Nuestro despacho presta asistencia jurídica en asuntos civiles, incluidos los relativos a créditos en moneda extranjera (CHF), créditos indexados al WIBOR y créditos sujetos a la sanción del crédito gratuito, en procedimientos de legalización de la estancia de extranjeros en Polonia, así como en todos los asuntos relacionados con los derechos de los animales.',
      '¿Qué nos distingue?:',
    ],
    bullets: [
  'enfoque individual y condiciones de cooperación claras y transparentes,',
  'asistencia completa en polaco, inglés y español,',
  'experiencia adquirida en despachos de abogados y entornos corporativos.',
],
    ctas: {
      primary: { label: 'Concertar una consulta', href: '#kontakt' },
      secondary: { label: 'Llamar ahora', href: 'tel:+48889054555' },
    },
    portraitAlt: 'Abogado Piotr Pieńkowski – retrato',
  },
  forWhom: {
  id: 'dla-kogo',
  title: '¿A quién ayudamos?',
  intro: '',
  cards: [
    {
      title: 'Prestatarios',
      description:
        'Ayudamos a prestatarios en litigios con bancos, especialmente en asuntos de créditos vinculados al CHF o al EUR, créditos basados en el WIBOR y casos relacionados con la sanción del crédito gratuito.',
    },
    {
      title: 'Extranjeros',
      description:
        'Asistimos a extranjeros en procedimientos administrativos y contencioso-administrativos relacionados con la legalización de la estancia y del trabajo en Polonia. Nos comunicamos con fluidez en inglés y en español.',
    },
    {
      title: 'Empresarios',
      description:
        'Ayudamos en la creación de actividades empresariales, preparamos contratos y representamos a clientes ante tribunales ordinarios, tribunales arbitrales o en mediación.',
    },
    {
      title: 'Otros clientes',
      description:
        'Asistimos en asuntos civiles y en asuntos relacionados con los derechos de los animales, incluidos casos de indemnización (por ejemplo, por mordeduras o por error veterinario), compensación por daños, protección de derechos personales, casos de maltrato animal, así como en asuntos de derecho de familia, como divorcios, pensiones alimenticias y contactos con hijos.',
    },
  ],
  linkText: '¿No ves tu caso aquí? Escríbenos y veremos en qué podemos ayudar.',
  },
  specialisations: {
    ...pl.specialisations,
    title: 'Áreas de práctica',
    intro:
      'Ofrecemos asistencia integral: desde consultas puntuales, contratos y dictámenes jurídicos, hasta la representación ante tribunales u organismos de la administración pública',
    items: [
      {
        title: 'Derechos de los animales',
description: 'Llevamos casos relacionados con los derechos de los animales, incluidos asuntos de maltrato animal, reclamaciones de indemnización o compensación (por ejemplo, por mordeduras), errores veterinarios y responsabilidad por vicios en la compraventa de animales.',
      },
      {
        title: 'Créditos en moneda extranjera (CHF / EUR)',
description: 'Representamos a clientes en litigios relacionados con créditos indexados o denominados en monedas extranjeras (CHF, EUR).',
      },
      {
        title: 'Sanción del crédito gratuito',
description: 'Asistimos a consumidores en la reclamación de la sanción del crédito gratuito cuando las entidades financieras no han cumplido con sus obligaciones legales.',
      },
      {
        title: 'Créditos basados en el WIBOR',
description: 'Asistimos a prestatarios en asuntos relacionados con créditos basados en el índice WIBOR, incluidos procedimientos para declarar la nulidad de dichos contratos o para recuperar cuotas pagadas en exceso.',
      },
      {
        title: 'Legalización de la estancia',
description: 'Asistimos a extranjeros en procedimientos de legalización de la estancia en Polonia, incluidos los procedimientos ante el voivoda y los procedimientos de recurso.',
      },
      {
        title: 'Apoyo a empresas',
description: 'Asistimos en la constitución de actividades empresariales, en la redacción de contratos y resoluciones, así como en la representación de clientes ante tribunales ordinarios, tribunales arbitrales y en procedimientos de mediación.',
      },
      {
        title: 'Insolvencia de personas físicas',
description: 'Asistimos a personas endeudadas en procedimientos de insolvencia de personas físicas, así como en actuaciones posteriores a la declaración de insolvencia.',
      },
      {
       title: 'Derecho civil',
description: 'Llevamos, entre otros, asuntos relativos a reclamaciones de pago derivadas de facturas, indemnizaciones por daños y perjuicios, compensaciones, así como casos relacionados con la responsabilidad por vicios de la cosa vendida.',
      },
      {
        title: 'Derecho sucesorio',
description: 'Nos ocupamos de asuntos relacionados con la herencia, la legítima, la partición de la herencia y la responsabilidad por las deudas hereditarias.',
      },
      {
        title: 'Derecho de familia',
description: 'Asistimos en asuntos de derecho de familia, incluidos divorcios, pensiones alimenticias y procedimientos relativos a contactos con hijos.',
      },
      {
        title: 'Contratos y dictámenes jurídicos',
description: 'Preparamos y analizamos contratos, así como elaboramos dictámenes jurídicos en el ámbito de nuestra práctica.',
      },
      {
        title: 'Representación judicial',
description: 'Representamos a nuestros clientes ante tribunales ordinarios, el Tribunal Supremo, tribunales administrativos, tribunales arbitrales y en procedimientos de mediación.',
      },
    ],
  },
  whyMe: {
  id: 'por-que',
  title: '¿Por qué colaborar con nosotros?',
  lead: '',
  usps: [
    {
      title: 'Condiciones claras de cooperación',
      description:
        'Antes de iniciar la colaboración, informamos al cliente sobre todos los costes, riesgos y posibles soluciones del problema.',
    },
    {
      title: 'Contacto constante',
      description:
        'Informamos de forma continua sobre cualquier cambio en el asunto, atendemos llamadas y respondemos a correos electrónicos o mensajes por WhatsApp.',
    },
    {
      title: 'Enfoque individual',
      description:
        'Tratamos cada asunto de manera individual. Nos importa encontrar una solución que realmente ayude al cliente.',
    },
    {
      title: 'Experiencia y compromiso',
      description:
        'Contamos con amplia experiencia en despachos de abogados y en entornos corporativos, comprendemos el trasfondo empresarial de los asuntos de nuestros clientes, trabajamos en tres idiomas y nos implicamos plenamente en cada asunto.',
    },
  ],
},

  aboutProfessional: {
    ...pl.aboutProfessional,
    title: 'Sobre nosotros',
    paragraphs: [
    'Pieńkowski – Despacho de Abogados fue fundado por el abogado Piotr Pieńkowski, graduado en Derecho por la Universidad de Varsovia y en Relaciones Económicas Internacionales por la Escuela de Economía de Varsovia (SGH).',
    'La combinación de Derecho y Economía nos permite comprender mejor el contexto empresarial de los asuntos de nuestros clientes.',
    'Abordamos cada asunto con compromiso y dedicación, tanto si se trata de la nulidad de un contrato de crédito, la redacción de resoluciones en una sociedad de responsabilidad limitada, la legalización de la estancia en Polonia o una reclamación de pago derivada, por ejemplo, de una mordedura de perro.',
    'La experiencia adquirida durante años en despachos de abogados y en entornos corporativos nos permite proponer soluciones reales y adecuadas a cada caso.',
    'Buscamos dichas soluciones teniendo en cuenta los matices de la situación del cliente, sin automatismos, proponiendo lo que, en nuestra opinión, resulta más beneficioso para el cliente.',
    '¡Le invitamos cordialmente a colaborar con nosotros!',
    ],
    blogCtaText: 'Conoce mi blog sobre derechos de los animales – Párrafo Zooprotector',
  },
  aboutPrivate: {
    ...pl.aboutPrivate,
    title: 'En lo personal',
    paragraphs: [
      'Fuera del trabajo viajo mucho: me gusta descubrir nuevos lugares y culturas. En mi tiempo libre leo buena literatura, toco la guitarra y me dedico a la fotografía.',
      'Estas aficiones me ayudan a mirar el mundo con más amplitud y a comprender mejor las historias de las personas con las que trabajo.',
    ],
  },
  testimonials: {
    ...pl.testimonials,
    items: [
      {
        quote:
          'Un enfoque muy serio del asunto, una comunicación clara y una explicación tranquila de todas las etapas del procedimiento.',
        author: 'Cliente particular – asunto de préstamo en divisa',
      },
      {
        quote:
          'Asesoramiento profesional y una implicación real. Sentí que mi asunto estaba en buenas manos.',
        author: 'Cliente empresarial',
      },
    ],
  },
  process: {
    ...pl.process,
    title: 'Cómo trabajamos',
  steps: [
    {
      title: 'Contacto',
      description: 'Comenzamos con el contacto por teléfono, correo electrónico o WhatsApp.',
    },
    {
      title: 'Análisis del asunto',
      description: 'Analizamos el asunto y determinamos en qué podemos ayudar.',
    },
    {
      title: 'Condiciones de cooperación',
      description: 'Acordamos el alcance de nuestra asistencia y la forma de facturación: las condiciones de cooperación son siempre claras y adaptadas a su situación.',
    },
    {
      title: 'Gestión del asunto',
      description: 'Ayudamos mediante negociaciones, la elaboración de dictámenes jurídicos y la representación ante tribunales u organismos administrativos. Informamos siempre de cualquier novedad en el asunto.',
    },
  ],
},
  blog: {
    ...pl.blog,
    teaser: {
      ...pl.blog.teaser,
      title: 'Blog',
      subtitle:
        'Artículos sobre derechos de los animales, créditos en moneda extranjera, créditos basados en el WIBOR y legalización de la estancia en Polonia',
      ctaLabel: 'Ver todos los artículos',
    },
  },
  contact: {
    ...pl.contact,
    title: 'Contacto',
    officeHeading: 'Datos del despacho',
    details: [
      { label: 'e-mail', value: 'biuro@pienkowskilegal.pl', href: 'mailto:biuro@pienkowskilegal.pl' },
      { label: 'teléfono', value: '+48 889 054 555', href: 'tel:+48889054555' },
      { label: 'dirección', value: 'ul. Bracka 20/7a, 00-028 Varsovia, Polonia' },
      { label: 'horario', value: 'lunes–viernes, 9:00–17:00 (CET)' },
    ],
    helper:
      '¿Desea agilizar nuestro contacto? Describa brevemente su problema (en unas pocas frases) utilizando el formulario.',
    fields: [
      { name: 'name', label: 'Nombre y apellidos', required: true },
      { name: 'email', label: 'E-mail', type: 'email', required: true },
      { name: 'subject', label: 'Asunto', required: true },
      { name: 'message', label: 'Mensaje', required: true },
    ],
   consentLabel: 'He leído la ',
consentLinkLabel: 'política de privacidad',
submitLabel: 'Enviar mensaje',

  },
  footer: {
    ...pl.footer,
    copyright:
      '© 2026 Pieńkowski – Despacho de Abogados. Todos los derechos reservados.',
    privacy: 'Política de privacidad y cookies',
    cookiesSettings: 'Configuración de cookies',
    terms: 'Condiciones de uso',
  },
};

export const content: Record<SupportedLocale, Content> = {
  pl,
  en,
  es,
};

export const getBlogPostBySlug = (slug: string): BlogPost | undefined =>
  blogPosts.find((post) => post.slug === slug);

export const localizeBlogPost = (
  post: BlogPost,
  locale: SupportedLocale
): BlogPost => {
  if (locale === 'pl' || !post.translations) return post;
  const tr = post.translations[locale];
  if (!tr) return post;
  return {
    ...post,
    title: tr.title ?? post.title,
    teaser: tr.teaser ?? post.teaser,
    sections: tr.sections ?? post.sections,
    ctaTitle: tr.ctaTitle ?? post.ctaTitle,
    ctaText: tr.ctaText ?? post.ctaText,
  };
};

export const getLocalizedBlogPostBySlug = (
  slug: string,
  locale: SupportedLocale
): BlogPost | undefined => {
  const base = getBlogPostBySlug(slug);
  return base ? localizeBlogPost(base, locale) : undefined;
};
